// FILE: store.ts
// Purpose: Normalizes orchestration snapshots into stable client state for the web app.
// Exports: Zustand store plus pure state transition helpers shared by runtime bootstrap flows.

import { Fragment, type ReactNode, createElement, useEffect } from "react";
import {
  type MessageId,
  type OrchestrationEvent,
  type OrchestrationLoadOlderThreadHistoryResult,
  type OrchestrationThreadHistoryCursor,
  type ProviderKind,
  ThreadId,
  type OrchestrationReadModel,
  type OrchestrationShellSnapshot,
  type OrchestrationShellStreamEvent,
  type OrchestrationSessionStatus,
  type TurnId,
  type UserId,
  type ManagedAgentId,
} from "@taskin-code/contracts";
import { resolveThreadBranchRegressionGuard } from "@taskin-code/shared/git";
import { resolveModelSlugForProvider } from "@taskin-code/shared/model";
import { upsertThreadActivity } from "@taskin-code/shared/threadActivities";
import { normalizeWorkspaceRootForComparison } from "@taskin-code/shared/threadWorkspace";
import { create } from "zustand";
import {
  type ChatAttachment,
  type ChatMessage,
  type Project,
  type SidebarThreadSummary,
  type Thread,
  type ThreadSession,
  type ThreadShell,
  type ThreadTurnState,
  type ThreadWorkspacePatch,
} from "./types";
import { Debouncer } from "@tanstack/react-pacer";
import { hasLiveTurnTailWork } from "./session-logic";
import { deriveThreadSummaryMetadata } from "@taskin-code/shared/threadSummary";
import { getThreadFromState, getThreadsFromState } from "./threadDerivation";
import { resolveMarkUnreadVisitedAt } from "./lib/threadInboxState";
import { attachmentPreviewRoutePath, toAttachmentPreviewUrl } from "./lib/attachmentUrls";

// ── State ────────────────────────────────────────────────────────────

export interface AppState {
  projects: Project[];
  threads: Thread[];
  sidebarThreadSummaryById: Record<string, SidebarThreadSummary>;
  threadsHydrated: boolean;
  /**
   * Which threads have had their full detail snapshot applied. The shell
   * snapshot carries titles and turn state but no transcript, so a thread can be
   * on screen while its messages are still in flight; without this flag that is
   * indistinguishable from a thread that genuinely has none.
   */
  threadDetailHydratedById?: Record<ThreadId, boolean>;
  /**
   * Threads held only because this person went and opened them — from the thread
   * directory, or straight from a URL somebody sent them.
   *
   * The server no longer pushes anybody threads that are not theirs, so these
   * arrive by explicit subscription and by nothing else. Two consequences, and
   * both are why the set has to exist rather than being inferred:
   *
   *   - Every list that offers a thread to open skips them. They are not in this
   *     person's sidebar, and putting them there is exactly what the directory
   *     exists to avoid.
   *   - The shell snapshot must not evict them. It rebuilds the thread tables
   *     from what it carries, so without this the open conversation would vanish
   *     on the next reconnect and the route would bounce back to `/`.
   */
  foreignThreadIds?: ReadonlySet<ThreadId>;
  threadIds?: ThreadId[];
  threadShellById?: Record<ThreadId, ThreadShell>;
  threadSessionById?: Record<ThreadId, ThreadSession | null>;
  threadTurnStateById?: Record<ThreadId, ThreadTurnState>;
  messageIdsByThreadId?: Record<ThreadId, MessageId[]>;
  messageByThreadId?: Record<ThreadId, Record<MessageId, ChatMessage>>;
  activityIdsByThreadId?: Record<ThreadId, string[]>;
  activityByThreadId?: Record<ThreadId, Record<string, Thread["activities"][number]>>;
  proposedPlanIdsByThreadId?: Record<ThreadId, string[]>;
  proposedPlanByThreadId?: Record<ThreadId, Record<string, Thread["proposedPlans"][number]>>;
  turnDiffIdsByThreadId?: Record<ThreadId, TurnId[]>;
  turnDiffSummaryByThreadId?: Record<ThreadId, Record<TurnId, Thread["turnDiffSummaries"][number]>>;
  /**
   * Whether a thread still has history older than what the store holds.
   *
   * The detail snapshot only ships the newest slice, so "the transcript starts
   * here" and "this is the whole conversation" are no longer the same thing —
   * and only the server can tell them apart, because the boundary is the
   * `/clear` or compaction floor, not the number of rows we happen to have.
   */
  hasMoreHistoryByThreadId?: Record<ThreadId, boolean>;
  /**
   * Where to resume paging backwards from, per thread. Opaque to the UI: it is
   * echoed back to `loadOlderThreadHistory` exactly as received.
   */
  oldestHistoryCursorByThreadId?: Record<ThreadId, OrchestrationThreadHistoryCursor>;
}

/**
 * The two history-window fields of a detail snapshot, travelling together.
 *
 * Optional because an older server omits them; a snapshot without them is
 * treated as "no paging available", which is exactly how the client behaved
 * before the window existed.
 */
export interface ThreadHistoryWindow {
  hasMoreHistory?: boolean | undefined;
  oldestCursor?: OrchestrationThreadHistoryCursor | undefined;
}

type ReadModelProject = OrchestrationReadModel["projects"][number];
type ReadModelThread = OrchestrationReadModel["threads"][number];
type ReadModelMessage = OrchestrationReadModel["threads"][number]["messages"][number];
type ShellSnapshotProject = OrchestrationShellSnapshot["projects"][number];
type ShellSnapshotThread = OrchestrationShellSnapshot["threads"][number];
type ThreadMessageSentEvent = Extract<OrchestrationEvent, { type: "thread.message-sent" }>;

const PERSISTED_STATE_KEY = "t3code:renderer-state:v8";
const LEGACY_PERSISTED_STATE_KEYS = [
  "t3code:renderer-state:v7",
  "t3code:renderer-state:v6",
  "t3code:renderer-state:v5",
  "t3code:renderer-state:v4",
  "t3code:renderer-state:v3",
  "codething:renderer-state:v4",
  "codething:renderer-state:v3",
  "codething:renderer-state:v2",
  "codething:renderer-state:v1",
] as const;
const MAX_THREAD_MESSAGES = 2_000;
const EMPTY_THREAD_IDS: ThreadId[] = [];
const EMPTY_THREAD_DETAIL_HYDRATED_BY_ID: Record<ThreadId, boolean> = {};
const EMPTY_THREAD_SHELL_BY_ID: Record<ThreadId, ThreadShell> = {};
const EMPTY_THREAD_SESSION_BY_ID: Record<ThreadId, ThreadSession | null> = {};
const EMPTY_THREAD_TURN_STATE_BY_ID: Record<ThreadId, ThreadTurnState> = {};
const EMPTY_MESSAGE_IDS_BY_THREAD: Record<ThreadId, MessageId[]> = {};
const EMPTY_MESSAGE_BY_THREAD: Record<ThreadId, Record<MessageId, ChatMessage>> = {};
const EMPTY_ACTIVITY_IDS_BY_THREAD: Record<ThreadId, string[]> = {};
const EMPTY_ACTIVITY_BY_THREAD: Record<ThreadId, Record<string, Thread["activities"][number]>> = {};
const EMPTY_PROPOSED_PLAN_IDS_BY_THREAD: Record<ThreadId, string[]> = {};
const EMPTY_PROPOSED_PLAN_BY_THREAD: Record<
  ThreadId,
  Record<string, Thread["proposedPlans"][number]>
> = {};
const EMPTY_HAS_MORE_HISTORY_BY_THREAD: Record<ThreadId, boolean> = {};
const EMPTY_OLDEST_HISTORY_CURSOR_BY_THREAD: Record<ThreadId, OrchestrationThreadHistoryCursor> =
  {};
const EMPTY_TURN_DIFF_IDS_BY_THREAD: Record<ThreadId, TurnId[]> = {};
/** Shared empty set, so a store that never opened a foreign thread allocates none. */
const EMPTY_FOREIGN_THREAD_IDS: ReadonlySet<ThreadId> = new Set<ThreadId>();
const EMPTY_TURN_DIFF_BY_THREAD: Record<
  ThreadId,
  Record<TurnId, Thread["turnDiffSummaries"][number]>
> = {};

const initialState: AppState = {
  projects: [],
  threads: [],
  sidebarThreadSummaryById: {},
  threadsHydrated: false,
  threadDetailHydratedById: {},
  foreignThreadIds: new Set<ThreadId>(),
  threadIds: [],
  threadShellById: {},
  threadSessionById: {},
  threadTurnStateById: {},
  messageIdsByThreadId: {},
  messageByThreadId: {},
  activityIdsByThreadId: {},
  activityByThreadId: {},
  proposedPlanIdsByThreadId: {},
  proposedPlanByThreadId: {},
  turnDiffIdsByThreadId: {},
  turnDiffSummaryByThreadId: {},
  hasMoreHistoryByThreadId: {},
  oldestHistoryCursorByThreadId: {},
};
const persistedExpandedProjectCwds = new Set<string>();
const persistedProjectOrderCwds: string[] = [];

function projectCwdKey(cwd: string): string {
  return normalizeWorkspaceRootForComparison(cwd);
}

function basenameOfPath(value: string): string | null {
  const segments = value.split(/[/\\]/).filter((segment) => segment.length > 0);
  return segments.at(-1) ?? null;
}

/**
 * Folder label shown beside a project's name (and as the rename placeholder).
 *
 * Projects added from GitHub live in a bare clone named `<projectId>.git`, so
 * the workspace root's basename is an internal UUID. Surfacing it reads as if
 * the project were named after its id — most visibly in the sidebar rename
 * input, whose placeholder is this value. Fall back to the server title there.
 */
function resolveProjectFolderName(incoming: {
  readonly id: string;
  readonly workspaceRoot: string;
  readonly title: string;
}): string {
  const basename = basenameOfPath(incoming.workspaceRoot);
  if (basename === null || basename.length === 0) return incoming.title;
  if (basename === incoming.id || basename === `${incoming.id}.git`) return incoming.title;
  return basename;
}

function rememberProjectUiState(projects: ReadonlyArray<Pick<Project, "cwd" | "expanded">>): void {
  for (const project of projects) {
    const cwdKey = projectCwdKey(project.cwd);
    if (project.expanded) {
      persistedExpandedProjectCwds.add(cwdKey);
    } else {
      persistedExpandedProjectCwds.delete(cwdKey);
    }
    if (!persistedProjectOrderCwds.includes(cwdKey)) {
      persistedProjectOrderCwds.push(cwdKey);
    }
  }
}

// ── Persist helpers ──────────────────────────────────────────────────

function readPersistedState(): AppState {
  if (typeof window === "undefined") return initialState;
  try {
    const raw = window.localStorage.getItem(PERSISTED_STATE_KEY);
    if (!raw) return initialState;
    const parsed = JSON.parse(raw) as {
      expandedProjectCwds?: string[];
      projectOrderCwds?: string[];
    };
    persistedExpandedProjectCwds.clear();
    persistedProjectOrderCwds.length = 0;
    for (const cwd of parsed.expandedProjectCwds ?? []) {
      if (typeof cwd === "string" && cwd.length > 0) {
        persistedExpandedProjectCwds.add(projectCwdKey(cwd));
      }
    }
    for (const cwd of parsed.projectOrderCwds ?? []) {
      const cwdKey = typeof cwd === "string" ? projectCwdKey(cwd) : "";
      if (cwdKey.length > 0 && !persistedProjectOrderCwds.includes(cwdKey)) {
        persistedProjectOrderCwds.push(cwdKey);
      }
    }
    return { ...initialState };
  } catch {
    return initialState;
  }
}

let legacyKeysCleanedUp = false;

function persistState(state: AppState): void {
  if (typeof window === "undefined") return;
  try {
    rememberProjectUiState(state.projects);
    window.localStorage.setItem(
      PERSISTED_STATE_KEY,
      JSON.stringify({
        expandedProjectCwds: state.projects
          .filter((project) => project.expanded)
          .map((project) => project.cwd),
        projectOrderCwds: state.projects.map((project) => project.cwd),
      }),
    );
    if (!legacyKeysCleanedUp) {
      legacyKeysCleanedUp = true;
      for (const legacyKey of LEGACY_PERSISTED_STATE_KEYS) {
        window.localStorage.removeItem(legacyKey);
      }
    }
  } catch {
    // Ignore quota/storage errors to avoid breaking chat UX.
  }
}
const debouncedPersistState = new Debouncer(persistState, { wait: 500 });

export function persistAppStateNow(state: AppState = useStore.getState()): void {
  persistState(state);
}

// ── Pure helpers ──────────────────────────────────────────────────────

function updateThread(
  threads: Thread[],
  threadId: ThreadId,
  updater: (t: Thread) => Thread,
): Thread[] {
  let changed = false;
  const next = threads.map((t) => {
    if (t.id !== threadId) return t;
    const updated = updater(t);
    if (updated !== t) changed = true;
    return updated;
  });
  return changed ? next : threads;
}

function sourceProposedPlansEqual(
  left: Thread["pendingSourceProposedPlan"],
  right: Thread["pendingSourceProposedPlan"],
): boolean {
  if (left === right) return true;
  if (left === undefined || right === undefined) return false;
  return left.threadId === right.threadId && left.planId === right.planId;
}

function latestTurnsEqual(left: Thread["latestTurn"], right: Thread["latestTurn"]): boolean {
  if (left === right) return true;
  if (left == null || right == null) return false;
  return (
    left.turnId === right.turnId &&
    left.state === right.state &&
    left.requestedAt === right.requestedAt &&
    left.startedAt === right.startedAt &&
    left.completedAt === right.completedAt &&
    left.assistantMessageId === right.assistantMessageId &&
    sourceProposedPlansEqual(left.sourceProposedPlan, right.sourceProposedPlan)
  );
}

function threadSessionsEqual(
  left: ThreadSession | null | undefined,
  right: ThreadSession | null | undefined,
): boolean {
  if (left === right) return true;
  if (left == null || right == null) return false;
  return (
    left.provider === right.provider &&
    left.status === right.status &&
    left.orchestrationStatus === right.orchestrationStatus &&
    left.activeTurnId === right.activeTurnId &&
    left.createdAt === right.createdAt &&
    left.updatedAt === right.updatedAt &&
    left.lastError === right.lastError
  );
}

/** Shared empty list so an unshared thread keeps a stable array identity. */
const EMPTY_ASSIGNED_USER_IDS: ReadonlyArray<UserId> = [];

function threadShellsEqual(left: ThreadShell | undefined, right: ThreadShell): boolean {
  return (
    left !== undefined &&
    left.id === right.id &&
    left.codexThreadId === right.codexThreadId &&
    left.projectId === right.projectId &&
    left.kind === right.kind &&
    left.title === right.title &&
    left.modelSelection === right.modelSelection &&
    left.runtimeMode === right.runtimeMode &&
    left.interactionMode === right.interactionMode &&
    left.error === right.error &&
    left.createdAt === right.createdAt &&
    (left.archivedAt ?? null) === (right.archivedAt ?? null) &&
    // Without this the cleared cut-off would be treated as "no change" and
    // never reach the store, leaving the hidden conversation on screen.
    (left.contextClearedAt ?? null) === (right.contextClearedAt ?? null) &&
    left.updatedAt === right.updatedAt &&
    left.envMode === right.envMode &&
    left.branch === right.branch &&
    left.worktreePath === right.worktreePath &&
    (left.associatedWorktreePath ?? null) === (right.associatedWorktreePath ?? null) &&
    (left.associatedWorktreeBranch ?? null) === (right.associatedWorktreeBranch ?? null) &&
    (left.associatedWorktreeRef ?? null) === (right.associatedWorktreeRef ?? null) &&
    (left.parentThreadId ?? null) === (right.parentThreadId ?? null) &&
    // Without this, linking (or inheriting) a Plane issue after the shell
    // already exists would look like "no change" and never reach the store,
    // leaving the header badge unrendered.
    (left.environmentId ?? null) === (right.environmentId ?? null) &&
    (left.planeIssueRef ?? null) === (right.planeIssueRef ?? null) &&
    (left.planeIssueId ?? null) === (right.planeIssueId ?? null) &&
    (left.linkedPullRequest?.number ?? null) === (right.linkedPullRequest?.number ?? null) &&
    (left.linkedPullRequest?.projectId ?? null) === (right.linkedPullRequest?.projectId ?? null) &&
    (left.subagentAgentId ?? null) === (right.subagentAgentId ?? null) &&
    (left.subagentNickname ?? null) === (right.subagentNickname ?? null) &&
    (left.subagentRole ?? null) === (right.subagentRole ?? null) &&
    (left.forkSourceThreadId ?? null) === (right.forkSourceThreadId ?? null) &&
    (left.handoff ?? null) === (right.handoff ?? null) &&
    left.latestUserMessageAt === right.latestUserMessageAt &&
    left.hasPendingApprovals === right.hasPendingApprovals &&
    left.hasPendingUserInput === right.hasPendingUserInput &&
    left.hasActionableProposedPlan === right.hasActionableProposedPlan &&
    (left.delegateReportReadAt ?? null) === (right.delegateReportReadAt ?? null) &&
    (left.delegationWakeSkippedAt ?? null) === (right.delegationWakeSkippedAt ?? null) &&
    left.lastVisitedAt === right.lastVisitedAt &&
    // Same trap as the fields below: a shell that compares equal is never
    // written, so an unlisted field cannot change once it has a value.
    (left.settledAt ?? null) === (right.settledAt ?? null) &&
    (left.snoozedUntil ?? null) === (right.snoozedUntil ?? null) &&
    (left.wokenAt ?? null) === (right.wokenAt ?? null) &&
    // By value, not by identity: every snapshot read rebuilds this object, so
    // comparing references would report a change on every poll.
    (left.recap?.generatedAt ?? null) === (right.recap?.generatedAt ?? null) &&
    (left.recap?.text ?? null) === (right.recap?.text ?? null) &&
    // Membership decides who sees the thread at all. Leaving it out here would
    // make a share look like "no change" and never reach the store — the same
    // trap the cleared cut-off fell into above.
    (left.createdBy ?? null) === (right.createdBy ?? null) &&
    (left.assignedUserIds ?? EMPTY_ASSIGNED_USER_IDS) ===
      (right.assignedUserIds ?? EMPTY_ASSIGNED_USER_IDS)
  );
}

function threadTurnStatesEqual(left: ThreadTurnState | undefined, right: ThreadTurnState): boolean {
  return (
    left !== undefined &&
    latestTurnsEqual(left.latestTurn, right.latestTurn) &&
    sourceProposedPlansEqual(left.pendingSourceProposedPlan, right.pendingSourceProposedPlan)
  );
}

function toThreadShell(thread: Thread): ThreadShell {
  return {
    id: thread.id,
    kind: thread.kind,
    codexThreadId: thread.codexThreadId,
    projectId: thread.projectId,
    title: thread.title,
    modelSelection: thread.modelSelection,
    runtimeMode: thread.runtimeMode,
    interactionMode: thread.interactionMode,
    error: thread.error,
    createdAt: thread.createdAt,
    archivedAt: thread.archivedAt ?? null,
    contextClearedAt: thread.contextClearedAt ?? null,
    updatedAt: thread.updatedAt,
    envMode: thread.envMode,
    branch: thread.branch,
    worktreePath: thread.worktreePath,
    associatedWorktreePath: thread.associatedWorktreePath ?? null,
    associatedWorktreeBranch: thread.associatedWorktreeBranch ?? null,
    associatedWorktreeRef: thread.associatedWorktreeRef ?? null,
    associatedAgentId: thread.associatedAgentId ?? null,
    parentThreadId: thread.parentThreadId ?? null,
    subagentAgentId: thread.subagentAgentId ?? null,
    subagentNickname: thread.subagentNickname ?? null,
    subagentRole: thread.subagentRole ?? null,
    forkSourceThreadId: thread.forkSourceThreadId ?? null,
    handoff: thread.handoff ?? null,
    environmentId: thread.environmentId ?? null,
    planeIssueRef: thread.planeIssueRef ?? null,
    planeIssueId: thread.planeIssueId ?? null,
    linkedPullRequest: thread.linkedPullRequest ?? null,
    ...(thread.latestUserMessageAt !== undefined
      ? { latestUserMessageAt: thread.latestUserMessageAt }
      : {}),
    ...(thread.hasPendingApprovals !== undefined
      ? { hasPendingApprovals: thread.hasPendingApprovals }
      : {}),
    ...(thread.hasPendingUserInput !== undefined
      ? { hasPendingUserInput: thread.hasPendingUserInput }
      : {}),
    ...(thread.hasActionableProposedPlan !== undefined
      ? { hasActionableProposedPlan: thread.hasActionableProposedPlan }
      : {}),
    ...(thread.delegateReportReadAt !== undefined
      ? { delegateReportReadAt: thread.delegateReportReadAt }
      : {}),
    ...(thread.delegationWakeSkippedAt !== undefined
      ? { delegationWakeSkippedAt: thread.delegationWakeSkippedAt }
      : {}),
    ...(thread.lastVisitedAt !== undefined ? { lastVisitedAt: thread.lastVisitedAt } : {}),
    // The shell is what `getThreadFromState` rebuilds a thread from, so a field
    // dropped here is lost on the next write, not merely absent from the shell.
    // That is how snoozing appeared to do nothing: the row was in the database
    // and gone from the store one update later.
    settledAt: thread.settledAt ?? null,
    snoozedUntil: thread.snoozedUntil ?? null,
    wokenAt: thread.wokenAt ?? null,
    recap: thread.recap ?? null,
    createdBy: thread.createdBy ?? null,
    assignedUserIds: thread.assignedUserIds ?? EMPTY_ASSIGNED_USER_IDS,
  };
}

function toThreadTurnState(thread: Thread): ThreadTurnState {
  return {
    latestTurn: thread.latestTurn,
    ...(thread.pendingSourceProposedPlan
      ? { pendingSourceProposedPlan: thread.pendingSourceProposedPlan }
      : {}),
  };
}

function buildMessageSlice(thread: Thread): {
  ids: MessageId[];
  byId: Record<MessageId, ChatMessage>;
} {
  return {
    ids: thread.messages.map((message) => message.id),
    byId: Object.fromEntries(
      thread.messages.map((message) => [message.id, message] as const),
    ) as Record<MessageId, ChatMessage>,
  };
}

function buildActivitySlice(thread: Thread): {
  ids: string[];
  byId: Record<string, Thread["activities"][number]>;
} {
  return {
    ids: thread.activities.map((activity) => activity.id),
    byId: Object.fromEntries(
      thread.activities.map((activity) => [activity.id, activity] as const),
    ) as Record<string, Thread["activities"][number]>,
  };
}

function buildProposedPlanSlice(thread: Thread): {
  ids: string[];
  byId: Record<string, Thread["proposedPlans"][number]>;
} {
  return {
    ids: thread.proposedPlans.map((plan) => plan.id),
    byId: Object.fromEntries(
      thread.proposedPlans.map((plan) => [plan.id, plan] as const),
    ) as Record<string, Thread["proposedPlans"][number]>,
  };
}

function buildTurnDiffSlice(thread: Thread): {
  ids: TurnId[];
  byId: Record<TurnId, Thread["turnDiffSummaries"][number]>;
} {
  return {
    ids: thread.turnDiffSummaries.map((summary) => summary.turnId),
    byId: Object.fromEntries(
      thread.turnDiffSummaries.map((summary) => [summary.turnId, summary] as const),
    ) as Record<TurnId, Thread["turnDiffSummaries"][number]>,
  };
}

// Reuse unchanged branches from the read model so per-thread selectors stay stable during streaming.
function arraysShallowEqual<T>(
  left: ReadonlyArray<T> | undefined,
  right: ReadonlyArray<T>,
): left is ReadonlyArray<T> {
  if (!left || left.length !== right.length) {
    return false;
  }
  for (let index = 0; index < left.length; index += 1) {
    if (left[index] !== right[index]) {
      return false;
    }
  }
  return true;
}

function recordsShallowEqual<T>(left: Record<string, T>, right: Record<string, T>): boolean {
  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);
  if (leftKeys.length !== rightKeys.length) {
    return false;
  }
  for (const key of leftKeys) {
    if (!(key in right) || left[key] !== right[key]) {
      return false;
    }
  }
  return true;
}

function deepEqualJson(left: unknown, right: unknown): boolean {
  if (Object.is(left, right)) {
    return true;
  }
  if (left == null || right == null || typeof left !== typeof right) {
    return false;
  }
  if (Array.isArray(left) || Array.isArray(right)) {
    if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length) {
      return false;
    }
    for (let index = 0; index < left.length; index += 1) {
      if (!deepEqualJson(left[index], right[index])) {
        return false;
      }
    }
    return true;
  }
  if (typeof left !== "object" || typeof right !== "object") {
    return false;
  }

  const leftRecord = left as Record<string, unknown>;
  const rightRecord = right as Record<string, unknown>;
  const leftKeys = Object.keys(leftRecord);
  const rightKeys = Object.keys(rightRecord);
  if (leftKeys.length !== rightKeys.length) {
    return false;
  }
  for (const key of leftKeys) {
    if (!(key in rightRecord) || !deepEqualJson(leftRecord[key], rightRecord[key])) {
      return false;
    }
  }
  return true;
}

function normalizeModelSelection<T extends { provider: ProviderKind; model: string }>(
  value: T,
  previous: T | null | undefined,
): T {
  const normalizedModel = resolveModelSlugForProvider(value.provider, value.model);
  const next = normalizedModel === value.model ? value : { ...value, model: normalizedModel };
  return previous && deepEqualJson(previous, next) ? previous : next;
}

function normalizeProjectScripts(
  incoming: ReadModelProject["scripts"],
  previous: Project["scripts"] | undefined,
): Project["scripts"] {
  const nextScripts = incoming.map((script, index) => {
    const existing = previous?.[index];
    return existing && deepEqualJson(existing, script) ? existing : script;
  });
  return arraysShallowEqual(previous, nextScripts) ? previous : nextScripts;
}

/**
 * Same identity-preserving pass as `normalizeProjectScripts`, for the local
 * command catalog: a project update that did not touch the catalog must not
 * hand every consumer a new array, or the settings panel re-renders on every
 * unrelated event.
 */
/**
 * Identity-preserving pass for the linked-project list.
 *
 * Plain ids, so `arraysShallowEqual` alone settles it — no per-entry deep
 * compare like the script and catalog normalizers need.
 */
function normalizeProjectLinkedProjectIds(
  incoming: ReadModelProject["linkedProjectIds"] | undefined,
  previous: Project["linkedProjectIds"] | undefined,
): Project["linkedProjectIds"] {
  const nextEntries = [...(incoming ?? [])];
  return arraysShallowEqual(previous, nextEntries) ? previous : nextEntries;
}

function normalizeProjectAgentIds(
  incoming: ReadModelProject["agentIds"] | undefined,
  previous: Project["agentIds"] | undefined,
): Project["agentIds"] {
  const nextEntries = [...(incoming ?? [])] as ManagedAgentId[];
  return arraysShallowEqual(previous, nextEntries) ? previous! : nextEntries;
}

function normalizeProjectLocalActions(
  incoming: ReadModelProject["localActions"] | undefined,
  previous: Project["localActions"] | undefined,
): Project["localActions"] {
  const nextEntries = (incoming ?? []).map((entry, index) => {
    const existing = previous?.[index];
    return existing && deepEqualJson(existing, entry) ? existing : entry;
  });
  return arraysShallowEqual(previous, nextEntries) ? previous : nextEntries;
}

/**
 * External-integration state (repository + Plane), identical on both project payloads.
 *
 * Kept in one place so the read-model and shell normalizers cannot disagree
 * about which fields survive — dropping them here is what left the settings
 * form blank for a repository the server had already validated. The Plane block
 * rides along for the same reason: its settings screen rehydrates from the
 * projection, so a field missing here is a field that silently resets.
 */
type ProjectIntegrationFields = Pick<
  Project,
  | "repositoryUrl"
  | "repositoryDefaultBranch"
  | "repoCredentialStatus"
  | "repoCredentialError"
  | "repoCredentialValidatedAt"
  | "planeProjectId"
  | "planeProjectIdentifier"
  | "planeWorkspaceSlug"
>;

/**
 * `undefined` on the incoming payload means "not carried", never "cleared".
 *
 * These fields are written outside the orchestration log — by the admin routes —
 * and reach the client only through the shell snapshot, which always states them
 * explicitly (`null` for absent). A read-model upsert, on the other hand, is
 * rebuilt from a domain-event payload that carries none of them, so taking its
 * `undefined` at face value would blank the Plane link and the repository state
 * on every rename until the next reload.
 */
function pickIntegrationField<T>(incoming: T | undefined, previous: T | undefined): T | undefined {
  return incoming !== undefined ? incoming : previous;
}

function projectIntegrationFields(
  incoming: ReadModelProject | ShellSnapshotProject,
  previous: Project | undefined,
): ProjectIntegrationFields {
  return {
    repositoryUrl: pickIntegrationField(incoming.repositoryUrl, previous?.repositoryUrl),
    repositoryDefaultBranch: pickIntegrationField(
      incoming.repositoryDefaultBranch,
      previous?.repositoryDefaultBranch,
    ),
    repoCredentialStatus: pickIntegrationField(
      incoming.repoCredentialStatus,
      previous?.repoCredentialStatus,
    ),
    repoCredentialError: pickIntegrationField(
      incoming.repoCredentialError,
      previous?.repoCredentialError,
    ),
    repoCredentialValidatedAt: pickIntegrationField(
      incoming.repoCredentialValidatedAt,
      previous?.repoCredentialValidatedAt,
    ),
    planeProjectId: pickIntegrationField(incoming.planeProjectId, previous?.planeProjectId),
    planeProjectIdentifier: pickIntegrationField(
      incoming.planeProjectIdentifier,
      previous?.planeProjectIdentifier,
    ),
    planeWorkspaceSlug: pickIntegrationField(
      incoming.planeWorkspaceSlug,
      previous?.planeWorkspaceSlug,
    ),
  };
}

function projectIntegrationFieldsUnchanged(
  previous: Project,
  resolved: ProjectIntegrationFields,
): boolean {
  return (
    previous.repositoryUrl === resolved.repositoryUrl &&
    previous.repositoryDefaultBranch === resolved.repositoryDefaultBranch &&
    previous.repoCredentialStatus === resolved.repoCredentialStatus &&
    previous.repoCredentialError === resolved.repoCredentialError &&
    previous.repoCredentialValidatedAt === resolved.repoCredentialValidatedAt &&
    previous.planeProjectId === resolved.planeProjectId &&
    previous.planeProjectIdentifier === resolved.planeProjectIdentifier &&
    previous.planeWorkspaceSlug === resolved.planeWorkspaceSlug
  );
}

function normalizeProjectFromReadModel(
  incoming: ReadModelProject,
  previous: Project | undefined,
): Project {
  const workspaceRootKey = projectCwdKey(incoming.workspaceRoot);
  const folderName = resolveProjectFolderName(incoming);
  const defaultModelSelection =
    incoming.defaultModelSelection === null
      ? null
      : normalizeModelSelection(incoming.defaultModelSelection, previous?.defaultModelSelection);
  const scripts = normalizeProjectScripts(incoming.scripts, previous?.scripts);
  const localActions = normalizeProjectLocalActions(incoming.localActions, previous?.localActions);
  const linkedProjectIds = normalizeProjectLinkedProjectIds(
    incoming.linkedProjectIds,
    previous?.linkedProjectIds,
  );
  const agentIds = normalizeProjectAgentIds(incoming.agentIds, previous?.agentIds);
  const expanded =
    previous?.expanded ??
    (persistedExpandedProjectCwds.size > 0
      ? persistedExpandedProjectCwds.has(workspaceRootKey)
      : true);
  const integrationFields = projectIntegrationFields(incoming, previous);

  if (
    previous &&
    previous.id === incoming.id &&
    previous.name === incoming.title &&
    previous.remoteName === incoming.title &&
    previous.folderName === folderName &&
    previous.cwd === incoming.workspaceRoot &&
    previous.defaultModelSelection === defaultModelSelection &&
    previous.expanded === expanded &&
    previous.createdAt === incoming.createdAt &&
    previous.updatedAt === incoming.updatedAt &&
    previous.scripts === scripts &&
    previous.localActions === localActions &&
    previous.linkedProjectIds === linkedProjectIds &&
    previous.agentIds === agentIds &&
    previous.isGitRepository === incoming.isGitRepository &&
    previous.isBareRepository === incoming.isBareRepository &&
    previous.baseBranch === incoming.baseBranch &&
    previous.iconFileName === (incoming.iconFileName ?? null) &&
    previous.color === (incoming.color ?? null) &&
    previous.description === (incoming.description ?? null) &&
    projectIntegrationFieldsUnchanged(previous, integrationFields)
  ) {
    return previous;
  }

  return {
    id: incoming.id,
    name: incoming.title,
    remoteName: incoming.title,
    folderName,
    iconFileName: incoming.iconFileName ?? null,
    color: incoming.color ?? null,
    description: incoming.description ?? null,
    cwd: incoming.workspaceRoot,
    defaultModelSelection,
    expanded,
    createdAt: incoming.createdAt,
    updatedAt: incoming.updatedAt,
    scripts,
    localActions,
    linkedProjectIds,
    agentIds,
    isGitRepository: incoming.isGitRepository,
    isBareRepository: incoming.isBareRepository,
    baseBranch: incoming.baseBranch,
    ...integrationFields,
  } satisfies Project;
}

function normalizeProjectFromShell(
  incoming: ShellSnapshotProject,
  previous: Project | undefined,
): Project {
  const workspaceRootKey = projectCwdKey(incoming.workspaceRoot);
  const folderName = resolveProjectFolderName(incoming);
  const defaultModelSelection =
    incoming.defaultModelSelection === null
      ? null
      : normalizeModelSelection(incoming.defaultModelSelection, previous?.defaultModelSelection);
  const scripts = normalizeProjectScripts(incoming.scripts, previous?.scripts);
  const localActions = normalizeProjectLocalActions(incoming.localActions, previous?.localActions);
  const linkedProjectIds = normalizeProjectLinkedProjectIds(
    incoming.linkedProjectIds,
    previous?.linkedProjectIds,
  );
  const agentIds = normalizeProjectAgentIds(incoming.agentIds, previous?.agentIds);
  const expanded =
    previous?.expanded ??
    (persistedExpandedProjectCwds.size > 0
      ? persistedExpandedProjectCwds.has(workspaceRootKey)
      : true);
  const integrationFields = projectIntegrationFields(incoming, previous);

  if (
    previous &&
    previous.id === incoming.id &&
    previous.name === incoming.title &&
    previous.remoteName === incoming.title &&
    previous.folderName === folderName &&
    previous.cwd === incoming.workspaceRoot &&
    previous.defaultModelSelection === defaultModelSelection &&
    previous.expanded === expanded &&
    previous.createdAt === incoming.createdAt &&
    previous.updatedAt === incoming.updatedAt &&
    previous.scripts === scripts &&
    previous.localActions === localActions &&
    previous.linkedProjectIds === linkedProjectIds &&
    previous.agentIds === agentIds &&
    previous.isGitRepository === incoming.isGitRepository &&
    previous.isBareRepository === incoming.isBareRepository &&
    previous.baseBranch === incoming.baseBranch &&
    previous.iconFileName === (incoming.iconFileName ?? null) &&
    previous.color === (incoming.color ?? null) &&
    previous.description === (incoming.description ?? null) &&
    projectIntegrationFieldsUnchanged(previous, integrationFields)
  ) {
    return previous;
  }

  return {
    id: incoming.id,
    name: incoming.title,
    remoteName: incoming.title,
    folderName,
    iconFileName: incoming.iconFileName ?? null,
    color: incoming.color ?? null,
    description: incoming.description ?? null,
    cwd: incoming.workspaceRoot,
    defaultModelSelection,
    expanded,
    createdAt: incoming.createdAt,
    updatedAt: incoming.updatedAt,
    scripts,
    localActions,
    linkedProjectIds,
    agentIds,
    isGitRepository: incoming.isGitRepository,
    isBareRepository: incoming.isBareRepository,
    baseBranch: incoming.baseBranch,
    ...integrationFields,
  } satisfies Project;
}

function upsertProjectFromReadModel(state: AppState, incoming: ReadModelProject): AppState {
  const existingProject = state.projects.find((project) => project.id === incoming.id);
  const nextProject = normalizeProjectFromReadModel(incoming, existingProject);

  if (existingProject) {
    if (existingProject === nextProject) {
      return state;
    }
    return {
      ...state,
      projects: state.projects.map((project) =>
        project.id === incoming.id ? nextProject : project,
      ),
    };
  }

  return {
    ...state,
    projects: [...state.projects, nextProject],
  };
}

function upsertProjectFromShell(state: AppState, incoming: ShellSnapshotProject): AppState {
  const existingProject =
    state.projects.find((project) => project.id === incoming.id) ??
    state.projects.find(
      (project) => projectCwdKey(project.cwd) === projectCwdKey(incoming.workspaceRoot),
    );
  const nextProject = normalizeProjectFromShell(incoming, existingProject);

  if (existingProject) {
    if (existingProject === nextProject) {
      return state;
    }
    return {
      ...state,
      projects: state.projects.map((project) =>
        project.id === existingProject.id ? nextProject : project,
      ),
    };
  }

  return {
    ...state,
    projects: [...state.projects, nextProject],
  };
}

function normalizeChatAttachments(
  incoming: ReadModelMessage["attachments"],
  previous: ChatAttachment[] | undefined,
): ChatAttachment[] | undefined {
  if (!incoming || incoming.length === 0) {
    return undefined;
  }

  const previousById = new Map(previous?.map((attachment) => [attachment.id, attachment] as const));
  const nextAttachments = incoming.map((attachment) => {
    const nextAttachment: ChatAttachment = {
      type: attachment.type,
      id: attachment.id,
      name: attachment.name,
      mimeType: attachment.mimeType,
      sizeBytes: attachment.sizeBytes,
      previewUrl: toAttachmentPreviewUrl(attachmentPreviewRoutePath(attachment.id)),
    };
    const existing = previousById.get(attachment.id);
    if (
      existing &&
      existing.type === nextAttachment.type &&
      existing.name === nextAttachment.name &&
      existing.mimeType === nextAttachment.mimeType &&
      existing.sizeBytes === nextAttachment.sizeBytes &&
      existing.previewUrl === nextAttachment.previewUrl
    ) {
      return existing;
    }
    return nextAttachment;
  });

  return arraysShallowEqual(previous, nextAttachments) ? previous : nextAttachments;
}

function normalizeChatMessage(
  incoming: ReadModelMessage,
  previous: ChatMessage | undefined,
): ChatMessage {
  const attachments = normalizeChatAttachments(incoming.attachments, previous?.attachments);
  const completedAt = incoming.streaming ? undefined : incoming.updatedAt;
  // Authorship never changes once a message exists, and most updates do not
  // carry it: streaming chunks and the `thread.message-sent` fold both rebuild
  // the message from a payload that has no author fields. Taking them from the
  // previous value keeps the optimistic paint — which is the only place the
  // display name is known before a reload — from being wiped by its own
  // server confirmation.
  const authorUserId = incoming.authorUserId ?? previous?.authorUserId;
  const authorDisplayName = incoming.authorDisplayName ?? previous?.authorDisplayName;
  if (
    previous &&
    previous.role === incoming.role &&
    previous.text === incoming.text &&
    previous.turnId === incoming.turnId &&
    previous.createdAt === incoming.createdAt &&
    previous.streaming === incoming.streaming &&
    previous.source === incoming.source &&
    previous.completedAt === completedAt &&
    previous.attachments === attachments &&
    previous.authorUserId === authorUserId &&
    previous.authorDisplayName === authorDisplayName
  ) {
    return previous;
  }

  return {
    id: incoming.id,
    role: incoming.role,
    text: incoming.text,
    turnId: incoming.turnId,
    createdAt: incoming.createdAt,
    streaming: incoming.streaming,
    source: incoming.source,
    ...(completedAt ? { completedAt } : {}),
    ...(attachments ? { attachments } : {}),
    ...(authorUserId ? { authorUserId } : {}),
    ...(authorDisplayName ? { authorDisplayName } : {}),
  };
}

function normalizeChatMessages(
  incoming: ReadModelThread["messages"],
  previous: ChatMessage[] | undefined,
): ChatMessage[] {
  const previousById = new Map(previous?.map((message) => [message.id, message] as const));
  const nextMessages = incoming.map((message) =>
    normalizeChatMessage(message, previousById.get(message.id)),
  );
  return arraysShallowEqual(previous, nextMessages) ? previous : nextMessages;
}

function readModelAttachmentsFromChatMessage(
  attachments: ChatMessage["attachments"],
): ReadModelThread["messages"][number]["attachments"] {
  return (
    attachments?.map((attachment) => ({
      id: attachment.id,
      name: attachment.name,
      type: attachment.type,
      mimeType: attachment.mimeType,
      sizeBytes: attachment.sizeBytes,
    })) ?? []
  );
}

function readModelMessageFromChatMessage(
  message: ChatMessage,
): ReadModelThread["messages"][number] {
  return {
    id: message.id,
    role: message.role,
    text: message.text,
    turnId: message.turnId ?? null,
    streaming: message.streaming,
    source: message.source ?? "native",
    createdAt: message.createdAt,
    updatedAt: message.completedAt ?? message.createdAt,
    attachments: readModelAttachmentsFromChatMessage(message.attachments),
  };
}

function shouldRetainLiveAssistantMessageForHotPath(
  previousThread: Thread,
  message: ChatMessage,
): boolean {
  if (message.role !== "assistant") {
    return false;
  }
  if (message.streaming) {
    return true;
  }
  const latestTurn = previousThread.latestTurn;
  if (!latestTurn) {
    return false;
  }
  if (latestTurn.assistantMessageId === message.id) {
    return true;
  }
  return (
    previousThread.session?.orchestrationStatus === "running" &&
    message.turnId !== undefined &&
    latestTurn.turnId === message.turnId
  );
}

function mergeReadModelMessagesWithLiveHotPath(
  incomingMessages: ReadModelThread["messages"],
  previousThread: Thread | undefined,
  options: {
    /** The snapshot already knows the live turn ended — see `snapshotSupersedesLiveTurn`. */
    liveTurnSuperseded: boolean;
  },
): ReadModelThread["messages"] {
  if (!previousThread || previousThread.messages.length === 0) {
    return incomingMessages;
  }

  const previousMessageById = new Map(
    previousThread.messages.map((message) => [message.id, message] as const),
  );
  const mergedById = new Map<MessageId, ReadModelThread["messages"][number]>();
  let changed = false;

  for (const incomingMessage of incomingMessages) {
    const previousMessage = previousMessageById.get(incomingMessage.id);
    if (!previousMessage || previousMessage.role !== incomingMessage.role) {
      mergedById.set(incomingMessage.id, incomingMessage);
      continue;
    }

    const incomingCompletedAt = incomingMessage.streaming ? undefined : incomingMessage.updatedAt;
    const shouldPreferLiveMessage =
      previousMessage.text.length > incomingMessage.text.length ||
      (!previousMessage.streaming && incomingMessage.streaming) ||
      (previousMessage.completedAt !== undefined &&
        (incomingCompletedAt === undefined || previousMessage.completedAt > incomingCompletedAt));

    if (!shouldPreferLiveMessage) {
      mergedById.set(incomingMessage.id, incomingMessage);
      continue;
    }

    changed = true;
    mergedById.set(incomingMessage.id, {
      ...incomingMessage,
      text: previousMessage.text,
      turnId: previousMessage.turnId ?? incomingMessage.turnId ?? null,
      source: previousMessage.source ?? incomingMessage.source ?? "native",
      // Keep the richer live text, but never the live `streaming` flag once the
      // snapshot has settled the turn: that flag is what kept `hasLiveAssistantIntro`
      // true forever and made the stuck spinner self-sustaining.
      streaming: options.liveTurnSuperseded ? incomingMessage.streaming : previousMessage.streaming,
      updatedAt: previousMessage.completedAt ?? incomingMessage.updatedAt,
      attachments: readModelAttachmentsFromChatMessage(previousMessage.attachments),
    });
  }

  for (const previousMessage of previousThread.messages) {
    if (mergedById.has(previousMessage.id)) {
      continue;
    }
    if (!shouldRetainLiveAssistantMessageForHotPath(previousThread, previousMessage)) {
      continue;
    }
    changed = true;
    mergedById.set(previousMessage.id, readModelMessageFromChatMessage(previousMessage));
  }

  if (!changed) {
    return incomingMessages;
  }

  return [...mergedById.values()].toSorted((left, right) =>
    left.createdAt === right.createdAt
      ? String(left.id).localeCompare(String(right.id))
      : left.createdAt.localeCompare(right.createdAt),
  );
}

function hasLiveAssistantIntro(previousThread: Thread | undefined): boolean {
  if (!previousThread) {
    return false;
  }
  const latestTurn = previousThread.latestTurn;
  if (!latestTurn || latestTurn.state !== "running") {
    return false;
  }
  if (previousThread.session?.orchestrationStatus !== "running") {
    return false;
  }
  return previousThread.messages.some(
    (message) =>
      message.role === "assistant" &&
      message.turnId === latestTurn.turnId &&
      (message.streaming || message.id === latestTurn.assistantMessageId),
  );
}

/**
 * Decides whether an incoming snapshot already knows how the live turn ended.
 *
 * The optimistic "running" state is a client-side guess kept alive so the
 * transcript does not flicker while the read model lags behind the live stream.
 * That guess must never outlive the server's verdict, which is what left threads
 * spinning forever: with a `streaming` flag stuck on a message, every later
 * snapshot was rewritten back to `running`, so neither a reconnect nor a full
 * refetch could clear it — only `/clear` or a page reload.
 *
 * Two facts make a snapshot authoritative, and both are expressed in server
 * time (session `updatedAt` values in the store always come from server
 * payloads, never from the client clock, so they are comparable):
 *
 * 1. Its session row is strictly newer than the one we hold and no longer says
 *    `running` — it was written after everything we know about, so it has seen
 *    the end of the turn. Ties still favour the live state, which is the actual
 *    lag case: the snapshot was built from the session row we already have while
 *    the stream had moved ahead.
 * 2. It reports our very turn as finished (`completedAt` set, or a terminal
 *    state). A lagging snapshot omits the turn or still shows it running; it
 *    never invents a completion.
 */
function snapshotSupersedesLiveTurn(incoming: ReadModelThread, previousThread: Thread): boolean {
  const previousSession = previousThread.session;
  if (
    previousSession &&
    incoming.session &&
    incoming.session.status !== "running" &&
    incoming.session.updatedAt > previousSession.updatedAt
  ) {
    return true;
  }
  const previousLatestTurn = previousThread.latestTurn;
  const incomingLatestTurn = incoming.latestTurn;
  return (
    previousLatestTurn !== null &&
    incomingLatestTurn !== null &&
    incomingLatestTurn.turnId === previousLatestTurn.turnId &&
    (incomingLatestTurn.completedAt !== null || incomingLatestTurn.state !== "running")
  );
}

type SettledTurnState = Exclude<NonNullable<Thread["latestTurn"]>["state"], "running">;

/**
 * The turn state implied by a session that stopped running, or `null` while the
 * session is still (or about to be) in flight and says nothing about the turn.
 */
function settledTurnStateForSessionStatus(
  status: OrchestrationSessionStatus,
): SettledTurnState | null {
  switch (status) {
    case "ready":
      return "completed";
    case "error":
      return "error";
    case "idle":
    case "stopped":
    case "interrupted":
      return "interrupted";
    case "running":
    case "starting":
      return null;
  }
}

/**
 * Closes a turn the client still believes is running once the session says
 * nothing is running anymore.
 *
 * Turning off the pulsing dot is not enough: `isLatestTurnSettled` also demands
 * a `completedAt`, so without one the active-turn layout (elapsed timer, blocked
 * composer) stays on screen forever, which is exactly what users saw when a turn
 * ended under a turn id the client no longer tracked.
 *
 * Turns that never started on the provider are left alone: they are still being
 * set up, and a `ready` session at that point only means the provider booted.
 */
function closeLatestTurnForSettledSession(
  latestTurn: Thread["latestTurn"],
  session: { status: OrchestrationSessionStatus; updatedAt: string } | null | undefined,
): Thread["latestTurn"] {
  if (!latestTurn || !session) {
    return latestTurn;
  }
  if (latestTurn.completedAt !== null && latestTurn.state !== "running") {
    return latestTurn;
  }
  if (latestTurn.startedAt === null) {
    return latestTurn;
  }
  const settledState = settledTurnStateForSessionStatus(session.status);
  if (settledState === null) {
    return latestTurn;
  }
  return {
    ...latestTurn,
    state: settledState,
    completedAt: latestTurn.completedAt ?? session.updatedAt,
  };
}

function readModelSessionFromThreadSession(
  previousSession: ThreadSession,
  previousThread: Thread | undefined,
  incomingSession: ReadModelThread["session"],
): NonNullable<ReadModelThread["session"]> {
  return {
    threadId: previousThread?.id ?? incomingSession?.threadId ?? ThreadId.makeUnsafe("unknown"),
    status: previousSession.orchestrationStatus,
    providerName: previousSession.provider,
    runtimeMode: previousThread?.runtimeMode ?? incomingSession?.runtimeMode ?? "full-access",
    activeTurnId: previousSession.activeTurnId ?? null,
    lastError: previousSession.lastError ?? null,
    updatedAt: previousSession.updatedAt,
  };
}

function mergeReadModelSessionWithLiveHotPath(
  incomingSession: ReadModelThread["session"],
  previousThread: Thread | undefined,
  options: {
    preserveRunningTurn: boolean;
  },
): ReadModelThread["session"] {
  const previousSession = previousThread?.session;
  if (!previousSession || !options.preserveRunningTurn) {
    return incomingSession;
  }
  if (!incomingSession) {
    return previousSession.orchestrationStatus === "running"
      ? readModelSessionFromThreadSession(previousSession, previousThread, incomingSession)
      : incomingSession;
  }
  if (previousSession.updatedAt > incomingSession.updatedAt) {
    const nextSession = readModelSessionFromThreadSession(
      previousSession,
      previousThread,
      incomingSession,
    );
    return {
      ...nextSession,
      providerName: incomingSession.providerName,
      runtimeMode: incomingSession.runtimeMode,
      activeTurnId: previousSession.activeTurnId ?? incomingSession.activeTurnId,
      lastError: previousSession.lastError ?? incomingSession.lastError,
    };
  }
  if (
    previousSession.orchestrationStatus === "running" &&
    incomingSession.status !== "running" &&
    previousSession.activeTurnId !== undefined
  ) {
    return {
      ...incomingSession,
      status: "running",
      activeTurnId: previousSession.activeTurnId,
      lastError: previousSession.lastError ?? incomingSession.lastError,
      updatedAt:
        previousSession.updatedAt >= incomingSession.updatedAt
          ? previousSession.updatedAt
          : incomingSession.updatedAt,
    };
  }
  return incomingSession;
}

function mergeReadModelLatestTurnWithLiveHotPath(
  incomingLatestTurn: ReadModelThread["latestTurn"],
  previousThread: Thread | undefined,
  options: {
    preserveRunningTurn: boolean;
  },
): ReadModelThread["latestTurn"] {
  const previousLatestTurn = previousThread?.latestTurn;
  if (!previousLatestTurn) {
    return incomingLatestTurn;
  }
  if (options.preserveRunningTurn) {
    if (incomingLatestTurn === null || incomingLatestTurn.turnId === previousLatestTurn.turnId) {
      return {
        ...(incomingLatestTurn ?? previousLatestTurn),
        turnId: previousLatestTurn.turnId,
        state: "running",
        requestedAt: incomingLatestTurn?.requestedAt ?? previousLatestTurn.requestedAt,
        startedAt: incomingLatestTurn?.startedAt ?? previousLatestTurn.startedAt,
        completedAt: null,
        assistantMessageId:
          previousLatestTurn.assistantMessageId ?? incomingLatestTurn?.assistantMessageId ?? null,
        ...((incomingLatestTurn?.sourceProposedPlan ?? previousLatestTurn.sourceProposedPlan)
          ? {
              sourceProposedPlan:
                incomingLatestTurn?.sourceProposedPlan ?? previousLatestTurn.sourceProposedPlan,
            }
          : {}),
      };
    }
    return incomingLatestTurn;
  }
  if (incomingLatestTurn === null || incomingLatestTurn.turnId !== previousLatestTurn.turnId) {
    return incomingLatestTurn;
  }
  if (
    previousLatestTurn.assistantMessageId === undefined ||
    incomingLatestTurn.assistantMessageId === previousLatestTurn.assistantMessageId
  ) {
    return incomingLatestTurn;
  }
  return {
    ...incomingLatestTurn,
    assistantMessageId: previousLatestTurn.assistantMessageId,
  };
}

function mergeReadModelThreadDetailWithLiveHotPath(
  incoming: ReadModelThread,
  previousThread: Thread | undefined,
): ReadModelThread {
  if (!previousThread) {
    return incoming;
  }

  const hasLiveTurn = hasLiveAssistantIntro(previousThread);
  const liveTurnSuperseded = hasLiveTurn && snapshotSupersedesLiveTurn(incoming, previousThread);
  const preserveRunningTurn = hasLiveTurn && !liveTurnSuperseded;
  const messages = mergeReadModelMessagesWithLiveHotPath(incoming.messages, previousThread, {
    liveTurnSuperseded,
  });
  const session = mergeReadModelSessionWithLiveHotPath(incoming.session, previousThread, {
    preserveRunningTurn,
  });
  const mergedLatestTurn = mergeReadModelLatestTurnWithLiveHotPath(
    incoming.latestTurn,
    previousThread,
    { preserveRunningTurn },
  );
  // The session row can settle before the latest-turn row catches up; once the
  // snapshot has overtaken our live guess, close the turn with it so the active
  // turn layout does not stay pinned on a turn nothing is running anymore.
  const latestTurn = liveTurnSuperseded
    ? closeLatestTurnForSettledSession(mergedLatestTurn, session)
    : mergedLatestTurn;
  if (
    messages === incoming.messages &&
    session === incoming.session &&
    latestTurn === incoming.latestTurn
  ) {
    return incoming;
  }
  return {
    ...incoming,
    messages,
    session,
    latestTurn,
  };
}

function normalizeProposedPlans(
  incoming: ReadModelThread["proposedPlans"],
  previous: Thread["proposedPlans"] | undefined,
): Thread["proposedPlans"] {
  const previousById = new Map(previous?.map((plan) => [plan.id, plan] as const));
  const nextPlans = incoming.map((plan) => {
    const existing = previousById.get(plan.id);
    if (
      existing &&
      existing.turnId === plan.turnId &&
      existing.planMarkdown === plan.planMarkdown &&
      existing.implementedAt === plan.implementedAt &&
      existing.implementationThreadId === plan.implementationThreadId &&
      existing.createdAt === plan.createdAt &&
      existing.updatedAt === plan.updatedAt
    ) {
      return existing;
    }
    return {
      id: plan.id,
      turnId: plan.turnId,
      planMarkdown: plan.planMarkdown,
      implementedAt: plan.implementedAt,
      implementationThreadId: plan.implementationThreadId,
      createdAt: plan.createdAt,
      updatedAt: plan.updatedAt,
    };
  });
  return arraysShallowEqual(previous, nextPlans) ? previous : nextPlans;
}

function normalizeTurnDiffFiles(
  incoming: ReadonlyArray<Thread["turnDiffSummaries"][number]["files"][number]>,
  previous: Thread["turnDiffSummaries"][number]["files"] | undefined,
): Thread["turnDiffSummaries"][number]["files"] {
  const nextFiles = incoming.map((file, index) => {
    const existing = previous?.[index];
    if (
      existing &&
      existing.path === file.path &&
      existing.kind === file.kind &&
      existing.additions === file.additions &&
      existing.deletions === file.deletions
    ) {
      return existing;
    }
    return file;
  });
  return arraysShallowEqual(previous, nextFiles) ? previous : nextFiles;
}

function normalizeTurnDiffSummaries(
  incoming: ReadModelThread["checkpoints"],
  previous: Thread["turnDiffSummaries"] | undefined,
): Thread["turnDiffSummaries"] {
  const previousByTurnId = new Map(previous?.map((summary) => [summary.turnId, summary] as const));
  const nextSummaries = incoming.map((checkpoint) => {
    const existing = previousByTurnId.get(checkpoint.turnId);
    const files = normalizeTurnDiffFiles(checkpoint.files, existing?.files);
    if (
      existing &&
      existing.completedAt === checkpoint.completedAt &&
      existing.status === checkpoint.status &&
      existing.assistantMessageId === (checkpoint.assistantMessageId ?? undefined) &&
      existing.checkpointTurnCount === checkpoint.checkpointTurnCount &&
      existing.checkpointRef === checkpoint.checkpointRef &&
      existing.files === files
    ) {
      return existing;
    }
    return {
      turnId: checkpoint.turnId,
      completedAt: checkpoint.completedAt,
      status: checkpoint.status,
      assistantMessageId: checkpoint.assistantMessageId ?? undefined,
      checkpointTurnCount: checkpoint.checkpointTurnCount,
      checkpointRef: checkpoint.checkpointRef,
      files,
    };
  });
  return arraysShallowEqual(previous, nextSummaries) ? previous : nextSummaries;
}

function normalizeActivities(
  incoming: ReadModelThread["activities"],
  previous: Thread["activities"] | undefined,
): Thread["activities"] {
  const previousById = new Map(previous?.map((activity) => [activity.id, activity] as const));
  const nextActivities = incoming.map((activity) => {
    const existing = previousById.get(activity.id);
    if (
      existing &&
      existing.kind === activity.kind &&
      existing.tone === activity.tone &&
      existing.summary === activity.summary &&
      deepEqualJson(existing.payload, activity.payload) &&
      existing.turnId === activity.turnId &&
      existing.sequence === activity.sequence &&
      existing.createdAt === activity.createdAt
    ) {
      return existing;
    }
    return activity;
  });
  return arraysShallowEqual(previous, nextActivities) ? previous : nextActivities;
}

function isNonFatalThreadErrorMessage(message: string | null | undefined): boolean {
  if (!message) {
    return false;
  }
  const normalized = message.trim().toLowerCase();
  return normalized.includes("write_stdin failed: stdin is closed for this session");
}

function normalizeThreadErrorMessage(message: string | null | undefined): string | null {
  return message && !isNonFatalThreadErrorMessage(message) ? message : null;
}

function normalizeThreadSession(
  incoming: ReadModelThread["session"],
  previous: Thread["session"] | undefined | null,
): Thread["session"] {
  if (!incoming) {
    return null;
  }
  const nextLastError =
    incoming.lastError && !isNonFatalThreadErrorMessage(incoming.lastError)
      ? incoming.lastError
      : undefined;
  const nextSession = {
    provider: toLegacyProvider(incoming.providerName),
    status: toLegacySessionStatus(incoming.status),
    orchestrationStatus: incoming.status,
    activeTurnId: incoming.activeTurnId ?? undefined,
    createdAt: incoming.updatedAt,
    updatedAt: incoming.updatedAt,
    ...(nextLastError ? { lastError: nextLastError } : {}),
  } satisfies NonNullable<Thread["session"]>;
  if (
    previous &&
    previous.provider === nextSession.provider &&
    previous.status === nextSession.status &&
    previous.orchestrationStatus === nextSession.orchestrationStatus &&
    previous.activeTurnId === nextSession.activeTurnId &&
    previous.createdAt === nextSession.createdAt &&
    previous.updatedAt === nextSession.updatedAt &&
    previous.lastError === nextSession.lastError
  ) {
    return previous;
  }
  return nextSession;
}

function normalizeLatestTurn(
  incoming: ReadModelThread["latestTurn"],
  previous: Thread["latestTurn"] | undefined | null,
): Thread["latestTurn"] {
  if (!incoming) {
    return null;
  }
  const nextSourceProposedPlan = incoming.sourceProposedPlan
    ? previous?.sourceProposedPlan &&
      previous.sourceProposedPlan.threadId === incoming.sourceProposedPlan.threadId &&
      previous.sourceProposedPlan.planId === incoming.sourceProposedPlan.planId
      ? previous.sourceProposedPlan
      : incoming.sourceProposedPlan
    : undefined;

  if (
    previous &&
    previous.turnId === incoming.turnId &&
    previous.state === incoming.state &&
    previous.requestedAt === incoming.requestedAt &&
    previous.startedAt === incoming.startedAt &&
    previous.completedAt === incoming.completedAt &&
    previous.assistantMessageId === incoming.assistantMessageId &&
    previous.sourceProposedPlan === nextSourceProposedPlan
  ) {
    return previous;
  }

  return {
    turnId: incoming.turnId,
    state: incoming.state,
    requestedAt: incoming.requestedAt,
    startedAt: incoming.startedAt,
    completedAt: incoming.completedAt,
    assistantMessageId: incoming.assistantMessageId,
    ...(nextSourceProposedPlan ? { sourceProposedPlan: nextSourceProposedPlan } : {}),
  };
}

/**
 * Keeps the previous array when the membership did not actually change.
 *
 * The sidebar re-renders off thread identity, and a fresh array on every
 * snapshot would make every thread look changed on every refresh.
 */
function normalizeAssignedUserIds(
  incoming: ReadonlyArray<UserId> | undefined,
  previous: ReadonlyArray<UserId> | undefined,
): ReadonlyArray<UserId> {
  const next = incoming ?? EMPTY_ASSIGNED_USER_IDS;
  if (previous && arraysShallowEqual(previous, next)) return previous;
  return next.length === 0 ? EMPTY_ASSIGNED_USER_IDS : next;
}

function normalizeThreadFromReadModel(
  incoming: ReadModelThread,
  previous: Thread | undefined,
): Thread {
  const modelSelection = normalizeModelSelection(incoming.modelSelection, previous?.modelSelection);
  const session = normalizeThreadSession(incoming.session, previous?.session);
  const messages = normalizeChatMessages(incoming.messages, previous?.messages);
  const proposedPlans = normalizeProposedPlans(incoming.proposedPlans, previous?.proposedPlans);
  const latestTurn = normalizeLatestTurn(incoming.latestTurn, previous?.latestTurn);
  const handoff =
    previous?.handoff && incoming.handoff && deepEqualJson(previous.handoff, incoming.handoff)
      ? previous.handoff
      : (incoming.handoff ?? null);
  const turnDiffSummaries = normalizeTurnDiffSummaries(
    incoming.checkpoints,
    previous?.turnDiffSummaries,
  );
  const activities = normalizeActivities(incoming.activities, previous?.activities);
  const error = normalizeThreadErrorMessage(incoming.session?.lastError);
  const lastVisitedAt = previous?.lastVisitedAt ?? incoming.updatedAt;
  const resolvedLatestUserMessageAt =
    Object.hasOwn(incoming, "latestUserMessageAt") && incoming.latestUserMessageAt !== undefined
      ? (incoming.latestUserMessageAt ?? null)
      : undefined;
  const resolvedHasPendingApprovals =
    typeof incoming.hasPendingApprovals === "boolean" ? incoming.hasPendingApprovals : undefined;
  const resolvedHasPendingUserInput =
    typeof incoming.hasPendingUserInput === "boolean" ? incoming.hasPendingUserInput : undefined;
  const resolvedHasActionableProposedPlan =
    typeof incoming.hasActionableProposedPlan === "boolean"
      ? incoming.hasActionableProposedPlan
      : undefined;
  const resolvedDelegateReportReadAt =
    Object.hasOwn(incoming, "delegateReportReadAt") && incoming.delegateReportReadAt !== undefined
      ? (incoming.delegateReportReadAt ?? null)
      : undefined;
  const resolvedDelegationWakeSkippedAt =
    Object.hasOwn(incoming, "delegationWakeSkippedAt") &&
    incoming.delegationWakeSkippedAt !== undefined
      ? (incoming.delegationWakeSkippedAt ?? null)
      : undefined;
  const resolvedBranch = resolveThreadBranchRegressionGuard({
    currentBranch: previous?.branch ?? null,
    nextBranch: incoming.branch,
  });
  const pendingSourceProposedPlan =
    latestTurn?.sourceProposedPlan ??
    (incoming.session?.status === "running" ? previous?.pendingSourceProposedPlan : undefined);
  const assignedUserIds = normalizeAssignedUserIds(
    incoming.assignedUserIds,
    previous?.assignedUserIds,
  );

  if (
    previous &&
    previous.projectId === incoming.projectId &&
    previous.kind === (incoming.kind ?? "standard") &&
    previous.title === incoming.title &&
    previous.modelSelection === modelSelection &&
    previous.runtimeMode === incoming.runtimeMode &&
    previous.interactionMode === incoming.interactionMode &&
    previous.session === session &&
    previous.messages === messages &&
    previous.proposedPlans === proposedPlans &&
    previous.error === error &&
    previous.createdAt === incoming.createdAt &&
    (previous.archivedAt ?? null) === (incoming.archivedAt ?? null) &&
    (previous.contextClearedAt ?? null) === (incoming.contextClearedAt ?? null) &&
    previous.updatedAt === incoming.updatedAt &&
    previous.latestTurn === latestTurn &&
    previous.pendingSourceProposedPlan === pendingSourceProposedPlan &&
    previous.lastVisitedAt === lastVisitedAt &&
    (previous.parentThreadId ?? null) === (incoming.parentThreadId ?? null) &&
    (previous.subagentAgentId ?? null) === (incoming.subagentAgentId ?? null) &&
    (previous.subagentNickname ?? null) === (incoming.subagentNickname ?? null) &&
    (previous.subagentRole ?? null) === (incoming.subagentRole ?? null) &&
    previous.envMode === (incoming.envMode ?? "local") &&
    previous.branch === resolvedBranch &&
    previous.worktreePath === incoming.worktreePath &&
    (previous.associatedWorktreePath ?? null) === (incoming.associatedWorktreePath ?? null) &&
    (previous.associatedWorktreeBranch ?? null) === (incoming.associatedWorktreeBranch ?? null) &&
    (previous.associatedWorktreeRef ?? null) === (incoming.associatedWorktreeRef ?? null) &&
    (previous.associatedAgentId ?? null) === (incoming.associatedAgentId ?? null) &&
    previous.latestUserMessageAt === resolvedLatestUserMessageAt &&
    previous.hasPendingApprovals === resolvedHasPendingApprovals &&
    previous.hasPendingUserInput === resolvedHasPendingUserInput &&
    previous.hasActionableProposedPlan === resolvedHasActionableProposedPlan &&
    previous.delegateReportReadAt === resolvedDelegateReportReadAt &&
    previous.delegationWakeSkippedAt === resolvedDelegationWakeSkippedAt &&
    (previous.forkSourceThreadId ?? null) === (incoming.forkSourceThreadId ?? null) &&
    (previous.environmentId ?? null) === (incoming.environmentId ?? null) &&
    (previous.planeIssueRef ?? null) === (incoming.planeIssueRef ?? null) &&
    (previous.planeIssueId ?? null) === (incoming.planeIssueId ?? null) &&
    (previous.linkedPullRequest?.number ?? null) === (incoming.linkedPullRequest?.number ?? null) &&
    (previous.linkedPullRequest?.projectId ?? null) ===
      (incoming.linkedPullRequest?.projectId ?? null) &&
    (previous.handoff ?? null) === handoff &&
    previous.turnDiffSummaries === turnDiffSummaries &&
    previous.activities === activities &&
    (previous.createdBy ?? null) === (incoming.createdBy ?? null) &&
    previous.assignedUserIds === assignedUserIds
  ) {
    return previous;
  }

  return {
    id: incoming.id,
    kind: incoming.kind ?? "standard",
    codexThreadId: null,
    projectId: incoming.projectId,
    title: incoming.title,
    modelSelection,
    runtimeMode: incoming.runtimeMode,
    interactionMode: incoming.interactionMode,
    session,
    messages,
    proposedPlans,
    error,
    createdAt: incoming.createdAt,
    archivedAt: incoming.archivedAt ?? null,
    contextClearedAt: incoming.contextClearedAt ?? null,
    updatedAt: incoming.updatedAt,
    latestTurn,
    ...(pendingSourceProposedPlan ? { pendingSourceProposedPlan } : {}),
    lastVisitedAt,
    // Per-person state has its own channel and never arrives on the shared
    // snapshot, so it is carried across rather than read off `incoming` —
    // which would blank someone's settled and snoozed threads on every shell
    // refresh, and refreshes are frequent.
    ...(previous?.settledAt != null ? { settledAt: previous.settledAt } : {}),
    ...(previous?.snoozedUntil != null ? { snoozedUntil: previous.snoozedUntil } : {}),
    ...(previous?.wokenAt != null ? { wokenAt: previous.wokenAt } : {}),
    // Carried across, and here that really is the only option: the read model's
    // thread has no `recap` field at all (only `OrchestrationThreadShell` does),
    // so there is nothing on `incoming` to read. Reading it off `incoming` here
    // would blank the row every time a thread detail arrived.
    ...(previous?.recap != null ? { recap: previous.recap } : {}),
    parentThreadId: incoming.parentThreadId ?? null,
    subagentAgentId: incoming.subagentAgentId ?? null,
    subagentNickname: incoming.subagentNickname ?? null,
    subagentRole: incoming.subagentRole ?? null,
    envMode: incoming.envMode ?? "local",
    branch: resolvedBranch,
    worktreePath: incoming.worktreePath,
    associatedWorktreePath: incoming.associatedWorktreePath ?? null,
    associatedWorktreeBranch: incoming.associatedWorktreeBranch ?? null,
    associatedWorktreeRef: incoming.associatedWorktreeRef ?? null,
    associatedAgentId: incoming.associatedAgentId ?? null,
    forkSourceThreadId: incoming.forkSourceThreadId ?? null,
    environmentId: incoming.environmentId ?? null,
    planeIssueRef: incoming.planeIssueRef ?? null,
    planeIssueId: incoming.planeIssueId ?? null,
    linkedPullRequest: incoming.linkedPullRequest ?? null,
    handoff,
    ...(resolvedLatestUserMessageAt !== undefined
      ? { latestUserMessageAt: resolvedLatestUserMessageAt }
      : {}),
    ...(resolvedHasPendingApprovals !== undefined
      ? { hasPendingApprovals: resolvedHasPendingApprovals }
      : {}),
    ...(resolvedHasPendingUserInput !== undefined
      ? { hasPendingUserInput: resolvedHasPendingUserInput }
      : {}),
    ...(resolvedHasActionableProposedPlan !== undefined
      ? { hasActionableProposedPlan: resolvedHasActionableProposedPlan }
      : {}),
    ...(resolvedDelegateReportReadAt !== undefined
      ? { delegateReportReadAt: resolvedDelegateReportReadAt }
      : {}),
    ...(resolvedDelegationWakeSkippedAt !== undefined
      ? { delegationWakeSkippedAt: resolvedDelegationWakeSkippedAt }
      : {}),
    turnDiffSummaries,
    activities,
    createdBy: incoming.createdBy ?? null,
    assignedUserIds,
  };
}

function normalizeThreadShellSnapshot(
  incoming: ShellSnapshotThread,
  previous: Thread | undefined,
): {
  shell: ThreadShell;
  session: ThreadSession | null;
  turnState: ThreadTurnState;
} {
  const modelSelection = normalizeModelSelection(incoming.modelSelection, previous?.modelSelection);
  const session = normalizeThreadSession(incoming.session, previous?.session);
  const latestTurn = normalizeLatestTurn(incoming.latestTurn, previous?.latestTurn);
  const handoff =
    previous?.handoff && incoming.handoff && deepEqualJson(previous.handoff, incoming.handoff)
      ? previous.handoff
      : (incoming.handoff ?? null);
  const error = normalizeThreadErrorMessage(incoming.session?.lastError);
  const lastVisitedAt = previous?.lastVisitedAt ?? incoming.updatedAt;
  const resolvedBranch = resolveThreadBranchRegressionGuard({
    currentBranch: previous?.branch ?? null,
    nextBranch: incoming.branch,
  });
  const shell: ThreadShell = {
    id: incoming.id,
    kind: incoming.kind ?? "standard",
    codexThreadId: previous?.codexThreadId ?? null,
    projectId: incoming.projectId,
    title: incoming.title,
    modelSelection,
    runtimeMode: incoming.runtimeMode,
    interactionMode: incoming.interactionMode,
    error,
    createdAt: incoming.createdAt,
    archivedAt: incoming.archivedAt ?? null,
    contextClearedAt: incoming.contextClearedAt ?? null,
    updatedAt: incoming.updatedAt,
    envMode: incoming.envMode ?? "local",
    branch: resolvedBranch,
    worktreePath: incoming.worktreePath,
    associatedWorktreePath: incoming.associatedWorktreePath ?? null,
    associatedWorktreeBranch: incoming.associatedWorktreeBranch ?? null,
    associatedWorktreeRef: incoming.associatedWorktreeRef ?? null,
    associatedAgentId: incoming.associatedAgentId ?? null,
    parentThreadId: incoming.parentThreadId ?? null,
    subagentAgentId: incoming.subagentAgentId ?? null,
    subagentNickname: incoming.subagentNickname ?? null,
    subagentRole: incoming.subagentRole ?? null,
    forkSourceThreadId: incoming.forkSourceThreadId ?? null,
    environmentId: incoming.environmentId ?? null,
    planeIssueRef: incoming.planeIssueRef ?? null,
    planeIssueId: incoming.planeIssueId ?? null,
    linkedPullRequest: incoming.linkedPullRequest ?? null,
    handoff,
    ...(incoming.latestUserMessageAt !== undefined
      ? { latestUserMessageAt: incoming.latestUserMessageAt ?? null }
      : {}),
    ...(incoming.hasPendingApprovals !== undefined
      ? { hasPendingApprovals: incoming.hasPendingApprovals }
      : {}),
    ...(incoming.hasPendingUserInput !== undefined
      ? { hasPendingUserInput: incoming.hasPendingUserInput }
      : {}),
    ...(incoming.hasActionableProposedPlan !== undefined
      ? { hasActionableProposedPlan: incoming.hasActionableProposedPlan }
      : {}),
    ...(incoming.delegateReportReadAt !== undefined
      ? { delegateReportReadAt: incoming.delegateReportReadAt ?? null }
      : {}),
    ...(incoming.delegationWakeSkippedAt !== undefined
      ? { delegationWakeSkippedAt: incoming.delegationWakeSkippedAt ?? null }
      : {}),
    ...(lastVisitedAt !== undefined ? { lastVisitedAt } : {}),
    createdBy: incoming.createdBy ?? null,
    // Per-person state has its own channel and never arrives on the shared
    // snapshot, so it is carried across rather than read off `incoming` —
    // which would blank someone's settled and snoozed threads on every shell
    // refresh, and refreshes are frequent.
    ...(previous?.settledAt != null ? { settledAt: previous.settledAt } : {}),
    ...(previous?.snoozedUntil != null ? { snoozedUntil: previous.snoozedUntil } : {}),
    ...(previous?.wokenAt != null ? { wokenAt: previous.wokenAt } : {}),
    // Read off `incoming`, unlike the three above it. Those are per-person state
    // that never travels on the shared snapshot, so carrying them across is the
    // only way they survive. The recap is the opposite: the shell snapshot and
    // every shell event carry it, and the schema gives it a decoding default, so
    // this field is always present here — `null` included, which is exactly what
    // an installation that turned the feature off sends and must be able to
    // blank the row with. Carrying `previous` across instead would mean the
    // value could never enter the store at all through this path.
    recap: incoming.recap ?? null,
    assignedUserIds: normalizeAssignedUserIds(incoming.assignedUserIds, previous?.assignedUserIds),
  };
  return {
    shell,
    session,
    turnState: {
      latestTurn,
      ...(latestTurn?.sourceProposedPlan
        ? { pendingSourceProposedPlan: latestTurn.sourceProposedPlan }
        : {}),
    },
  };
}

function mapProjectsFromReadModel(
  incoming: OrchestrationReadModel["projects"],
  previous: Project[],
): Project[] {
  const previousById = new Map(previous.map((project) => [project.id, project] as const));
  const previousByCwd = new Map(
    previous.map((project) => [projectCwdKey(project.cwd), project] as const),
  );
  const previousOrderById = new Map(previous.map((project, index) => [project.id, index] as const));
  const previousOrderByCwd = new Map(
    previous.map((project, index) => [projectCwdKey(project.cwd), index] as const),
  );
  const persistedOrderByCwd = new Map(
    persistedProjectOrderCwds.map((cwd, index) => [cwd, index] as const),
  );
  const usePersistedOrder = previous.length === 0;

  const mappedProjects = incoming
    .map((project) => {
      const existing =
        previousById.get(project.id) ?? previousByCwd.get(projectCwdKey(project.workspaceRoot));
      return normalizeProjectFromReadModel(project, existing);
    })
    .map((project, incomingIndex) => {
      const previousIndex =
        previousOrderById.get(project.id) ?? previousOrderByCwd.get(projectCwdKey(project.cwd));
      const persistedIndex = usePersistedOrder
        ? persistedOrderByCwd.get(projectCwdKey(project.cwd))
        : undefined;
      const orderIndex =
        previousIndex ??
        persistedIndex ??
        (usePersistedOrder ? persistedProjectOrderCwds.length : previous.length) + incomingIndex;
      return { project, incomingIndex, orderIndex };
    })
    .toSorted((a, b) => {
      const byOrder = a.orderIndex - b.orderIndex;
      if (byOrder !== 0) return byOrder;
      return a.incomingIndex - b.incomingIndex;
    })
    .map((entry) => entry.project);

  return arraysShallowEqual(previous, mappedProjects) ? previous : mappedProjects;
}

function mapProjectsFromShellSnapshot(
  incoming: OrchestrationShellSnapshot["projects"],
  previous: Project[],
): Project[] {
  const previousById = new Map(previous.map((project) => [project.id, project] as const));
  const previousByCwd = new Map(
    previous.map((project) => [projectCwdKey(project.cwd), project] as const),
  );
  const previousOrderById = new Map(previous.map((project, index) => [project.id, index] as const));
  const previousOrderByCwd = new Map(
    previous.map((project, index) => [projectCwdKey(project.cwd), index] as const),
  );
  const persistedOrderByCwd = new Map(
    persistedProjectOrderCwds.map((cwd, index) => [cwd, index] as const),
  );
  const usePersistedOrder = previous.length === 0;

  const mappedProjects = incoming
    .map((project) => {
      const existing =
        previousById.get(project.id) ?? previousByCwd.get(projectCwdKey(project.workspaceRoot));
      return normalizeProjectFromShell(project, existing);
    })
    .map((project, incomingIndex) => {
      const previousIndex =
        previousOrderById.get(project.id) ?? previousOrderByCwd.get(projectCwdKey(project.cwd));
      const persistedIndex = usePersistedOrder
        ? persistedOrderByCwd.get(projectCwdKey(project.cwd))
        : undefined;
      const orderIndex =
        previousIndex ??
        persistedIndex ??
        (usePersistedOrder ? persistedProjectOrderCwds.length : previous.length) + incomingIndex;
      return { project, incomingIndex, orderIndex };
    })
    .toSorted((a, b) => {
      const byOrder = a.orderIndex - b.orderIndex;
      if (byOrder !== 0) return byOrder;
      return a.incomingIndex - b.incomingIndex;
    })
    .map((entry) => entry.project);

  return arraysShallowEqual(previous, mappedProjects) ? previous : mappedProjects;
}

function toLegacySessionStatus(
  status: OrchestrationSessionStatus,
): "connecting" | "ready" | "running" | "error" | "closed" {
  switch (status) {
    case "starting":
      return "connecting";
    case "running":
      return "running";
    case "error":
      return "error";
    case "ready":
    case "interrupted":
      return "ready";
    case "idle":
    case "stopped":
      return "closed";
  }
}

function toLegacyProvider(providerName: string | null): ProviderKind {
  if (providerName === "codex" || providerName === "claudeAgent") {
    return providerName;
  }
  return "codex";
}

function resolveThreadSidebarMetadata(
  thread: Thread,
): Pick<
  SidebarThreadSummary,
  | "latestUserMessageAt"
  | "hasPendingApprovals"
  | "hasPendingUserInput"
  | "hasActionableProposedPlan"
  | "hasLiveTailWork"
  | "delegateReportReadAt"
  | "delegationWakeSkippedAt"
> {
  const derivedMetadata = deriveThreadSummaryMetadata({
    messages: thread.messages,
    activities: thread.activities,
    proposedPlans: thread.proposedPlans,
    latestTurn: thread.latestTurn,
  });

  return {
    latestUserMessageAt: thread.latestUserMessageAt ?? derivedMetadata.latestUserMessageAt,
    hasPendingApprovals: thread.hasPendingApprovals ?? derivedMetadata.hasPendingApprovals,
    hasPendingUserInput: thread.hasPendingUserInput ?? derivedMetadata.hasPendingUserInput,
    hasActionableProposedPlan:
      thread.hasActionableProposedPlan ?? derivedMetadata.hasActionableProposedPlan,
    // The shell's receipt wins when it has one: a thread the client holds only
    // as a shell has no activities to derive it from, and falling back to the
    // derivation would report every such delegation as never read.
    delegateReportReadAt: thread.delegateReportReadAt ?? derivedMetadata.delegateReportReadAt,
    // Same reading for the wake decision: the shell carries it for threads the
    // client never held in full.
    delegationWakeSkippedAt:
      thread.delegationWakeSkippedAt ?? derivedMetadata.delegationWakeSkippedAt,
    hasLiveTailWork: Boolean(
      hasLiveTurnTailWork({
        latestTurn: thread.latestTurn,
        messages: thread.messages,
        activities: thread.activities,
        session: thread.session,
      }),
    ),
  };
}

function sidebarThreadSummariesEqual(
  left: SidebarThreadSummary | undefined,
  right: SidebarThreadSummary,
): boolean {
  return (
    left !== undefined &&
    left.id === right.id &&
    left.projectId === right.projectId &&
    (left.kind ?? "standard") === (right.kind ?? "standard") &&
    left.title === right.title &&
    left.modelSelection === right.modelSelection &&
    left.interactionMode === right.interactionMode &&
    left.envMode === right.envMode &&
    left.branch === right.branch &&
    left.worktreePath === right.worktreePath &&
    left.session === right.session &&
    left.createdAt === right.createdAt &&
    (left.archivedAt ?? null) === (right.archivedAt ?? null) &&
    left.updatedAt === right.updatedAt &&
    left.latestTurn === right.latestTurn &&
    left.lastVisitedAt === right.lastVisitedAt &&
    (left.parentThreadId ?? null) === (right.parentThreadId ?? null) &&
    (left.subagentAgentId ?? null) === (right.subagentAgentId ?? null) &&
    (left.subagentNickname ?? null) === (right.subagentNickname ?? null) &&
    (left.subagentRole ?? null) === (right.subagentRole ?? null) &&
    left.latestUserMessageAt === right.latestUserMessageAt &&
    left.hasPendingApprovals === right.hasPendingApprovals &&
    left.hasPendingUserInput === right.hasPendingUserInput &&
    left.hasActionableProposedPlan === right.hasActionableProposedPlan &&
    left.hasLiveTailWork === right.hasLiveTailWork &&
    (left.delegateReportReadAt ?? null) === (right.delegateReportReadAt ?? null) &&
    (left.delegationWakeSkippedAt ?? null) === (right.delegationWakeSkippedAt ?? null) &&
    (left.forkSourceThreadId ?? null) === (right.forkSourceThreadId ?? null) &&
    (left.handoff ?? null) === (right.handoff ?? null) &&
    // Compared as well as copied: this equality decides whether the summary is
    // rebuilt at all, so leaving these out would keep handing back the stale
    // object and the row would never change section.
    (left.settledAt ?? null) === (right.settledAt ?? null) &&
    (left.snoozedUntil ?? null) === (right.snoozedUntil ?? null) &&
    (left.wokenAt ?? null) === (right.wokenAt ?? null) &&
    (left.recap?.generatedAt ?? null) === (right.recap?.generatedAt ?? null) &&
    (left.recap?.text ?? null) === (right.recap?.text ?? null) &&
    // By value: the snapshot rebuilds the reference object on every read, so comparing identity
    // would report a change on every poll and rebuild every row.
    (left.linkedPullRequest?.number ?? null) === (right.linkedPullRequest?.number ?? null) &&
    (left.linkedPullRequest?.projectId ?? null) === (right.linkedPullRequest?.projectId ?? null)
  );
}

// Keep sidebar row state lightweight so live thread updates do not force row code
// to rescan every thread message/activity collection on each render.
function buildSidebarThreadSummary(
  thread: Thread,
  previous?: SidebarThreadSummary,
): SidebarThreadSummary {
  const metadata = resolveThreadSidebarMetadata(thread);
  const nextSummary: SidebarThreadSummary = {
    id: thread.id,
    projectId: thread.projectId,
    kind: thread.kind,
    title: thread.title,
    modelSelection: thread.modelSelection,
    interactionMode: thread.interactionMode,
    envMode: thread.envMode,
    branch: thread.branch,
    worktreePath: thread.worktreePath,
    session: thread.session,
    createdAt: thread.createdAt,
    archivedAt: thread.archivedAt ?? null,
    updatedAt: thread.updatedAt,
    latestTurn: thread.latestTurn,
    lastVisitedAt: thread.lastVisitedAt,
    // This summary is built field by field, so anything not named here never
    // reaches the sidebar. Both of these decide which section a row lands in.
    settledAt: thread.settledAt ?? null,
    snoozedUntil: thread.snoozedUntil ?? null,
    wokenAt: thread.wokenAt ?? null,
    recap: thread.recap ?? null,
    parentThreadId: thread.parentThreadId ?? null,
    subagentAgentId: thread.subagentAgentId ?? null,
    subagentNickname: thread.subagentNickname ?? null,
    subagentRole: thread.subagentRole ?? null,
    latestUserMessageAt: metadata.latestUserMessageAt,
    hasPendingApprovals: metadata.hasPendingApprovals,
    hasPendingUserInput: metadata.hasPendingUserInput,
    hasActionableProposedPlan: metadata.hasActionableProposedPlan,
    hasLiveTailWork: metadata.hasLiveTailWork,
    delegateReportReadAt: metadata.delegateReportReadAt,
    delegationWakeSkippedAt: metadata.delegationWakeSkippedAt,
    forkSourceThreadId: thread.forkSourceThreadId ?? null,
    handoff: thread.handoff ?? null,
    linkedPullRequest: thread.linkedPullRequest ?? null,
  };
  if (previous && sidebarThreadSummariesEqual(previous, nextSummary)) {
    return previous;
  }
  return nextSummary;
}

function ensureThreadRegistered(state: AppState, threadId: ThreadId): AppState {
  const threadIds = state.threadIds ?? EMPTY_THREAD_IDS;
  if (threadIds.includes(threadId)) {
    return state;
  }
  return {
    ...state,
    threadIds: [...threadIds, threadId],
  };
}

function retainThreadScopedRecord<T>(
  record: Record<ThreadId, T> | undefined,
  nextThreadIds: ReadonlySet<ThreadId>,
): Record<ThreadId, T> {
  if (!record) {
    return {};
  }
  let changed = false;
  const nextRecord: Record<ThreadId, T> = {};
  for (const [threadId, value] of Object.entries(record) as [ThreadId, T][]) {
    if (!nextThreadIds.has(threadId)) {
      changed = true;
      continue;
    }
    nextRecord[threadId] = value;
  }
  return changed ? nextRecord : record;
}

/**
 * Records that a thread's transcript arrived. Called only from the detail paths:
 * shell updates deliberately leave the flag alone, because they carry no messages.
 */
function markThreadDetailHydrated(state: AppState, threadId: ThreadId): AppState {
  const current = state.threadDetailHydratedById ?? EMPTY_THREAD_DETAIL_HYDRATED_BY_ID;
  if (current[threadId]) {
    return state;
  }
  return {
    ...state,
    threadDetailHydratedById: { ...current, [threadId]: true },
  };
}

function writeThreadShellProjection(
  state: AppState,
  nextThread: {
    shell: ThreadShell;
    session: ThreadSession | null;
    turnState: ThreadTurnState;
  },
): AppState {
  const previousShell = state.threadShellById?.[nextThread.shell.id];
  let nextState = ensureThreadRegistered(state, nextThread.shell.id);

  if (!threadShellsEqual(previousShell, nextThread.shell)) {
    nextState = {
      ...nextState,
      threadShellById: {
        ...(nextState.threadShellById ?? EMPTY_THREAD_SHELL_BY_ID),
        [nextThread.shell.id]: nextThread.shell,
      },
    };
  }

  if (
    !threadSessionsEqual(
      (nextState.threadSessionById ?? EMPTY_THREAD_SESSION_BY_ID)[nextThread.shell.id] ?? null,
      nextThread.session,
    )
  ) {
    nextState = {
      ...nextState,
      threadSessionById: {
        ...(nextState.threadSessionById ?? EMPTY_THREAD_SESSION_BY_ID),
        [nextThread.shell.id]: nextThread.session,
      },
    };
  }

  if (
    !threadTurnStatesEqual(
      (nextState.threadTurnStateById ?? EMPTY_THREAD_TURN_STATE_BY_ID)[nextThread.shell.id],
      nextThread.turnState,
    )
  ) {
    nextState = {
      ...nextState,
      threadTurnStateById: {
        ...(nextState.threadTurnStateById ?? EMPTY_THREAD_TURN_STATE_BY_ID),
        [nextThread.shell.id]: nextThread.turnState,
      },
    };
  }

  return nextState;
}

function writeThreadState(state: AppState, nextThread: Thread, previousThread?: Thread): AppState {
  const nextShell = toThreadShell(nextThread);
  const nextTurnState = toThreadTurnState(nextThread);
  const previousShell = state.threadShellById?.[nextThread.id];
  const previousTurnState = state.threadTurnStateById?.[nextThread.id];

  let nextState = ensureThreadRegistered(state, nextThread.id);

  if (!threadShellsEqual(previousShell, nextShell)) {
    nextState = {
      ...nextState,
      threadShellById: {
        ...(nextState.threadShellById ?? EMPTY_THREAD_SHELL_BY_ID),
        [nextThread.id]: nextShell,
      },
    };
  }

  if (!threadSessionsEqual(previousThread?.session ?? null, nextThread.session)) {
    nextState = {
      ...nextState,
      threadSessionById: {
        ...(nextState.threadSessionById ?? EMPTY_THREAD_SESSION_BY_ID),
        [nextThread.id]: nextThread.session,
      },
    };
  }

  if (!threadTurnStatesEqual(previousTurnState, nextTurnState)) {
    nextState = {
      ...nextState,
      threadTurnStateById: {
        ...(nextState.threadTurnStateById ?? EMPTY_THREAD_TURN_STATE_BY_ID),
        [nextThread.id]: nextTurnState,
      },
    };
  }

  if (previousThread?.messages !== nextThread.messages) {
    const nextMessageSlice = buildMessageSlice(nextThread);
    nextState = {
      ...nextState,
      messageIdsByThreadId: {
        ...(nextState.messageIdsByThreadId ?? EMPTY_MESSAGE_IDS_BY_THREAD),
        [nextThread.id]: nextMessageSlice.ids,
      },
      messageByThreadId: {
        ...(nextState.messageByThreadId ?? EMPTY_MESSAGE_BY_THREAD),
        [nextThread.id]: nextMessageSlice.byId,
      },
    };
  }

  if (previousThread?.activities !== nextThread.activities) {
    const nextActivitySlice = buildActivitySlice(nextThread);
    nextState = {
      ...nextState,
      activityIdsByThreadId: {
        ...(nextState.activityIdsByThreadId ?? EMPTY_ACTIVITY_IDS_BY_THREAD),
        [nextThread.id]: nextActivitySlice.ids,
      },
      activityByThreadId: {
        ...(nextState.activityByThreadId ?? EMPTY_ACTIVITY_BY_THREAD),
        [nextThread.id]: nextActivitySlice.byId,
      },
    };
  }

  if (previousThread?.proposedPlans !== nextThread.proposedPlans) {
    const nextProposedPlanSlice = buildProposedPlanSlice(nextThread);
    nextState = {
      ...nextState,
      proposedPlanIdsByThreadId: {
        ...(nextState.proposedPlanIdsByThreadId ?? EMPTY_PROPOSED_PLAN_IDS_BY_THREAD),
        [nextThread.id]: nextProposedPlanSlice.ids,
      },
      proposedPlanByThreadId: {
        ...(nextState.proposedPlanByThreadId ?? EMPTY_PROPOSED_PLAN_BY_THREAD),
        [nextThread.id]: nextProposedPlanSlice.byId,
      },
    };
  }

  if (previousThread?.turnDiffSummaries !== nextThread.turnDiffSummaries) {
    const nextTurnDiffSlice = buildTurnDiffSlice(nextThread);
    nextState = {
      ...nextState,
      turnDiffIdsByThreadId: {
        ...(nextState.turnDiffIdsByThreadId ?? EMPTY_TURN_DIFF_IDS_BY_THREAD),
        [nextThread.id]: nextTurnDiffSlice.ids,
      },
      turnDiffSummaryByThreadId: {
        ...(nextState.turnDiffSummaryByThreadId ?? EMPTY_TURN_DIFF_BY_THREAD),
        [nextThread.id]: nextTurnDiffSlice.byId,
      },
    };
  }

  return nextState;
}

function removeThreadState(state: AppState, threadId: ThreadId): AppState {
  const { [threadId]: _removedShell, ...threadShellById } =
    state.threadShellById ?? EMPTY_THREAD_SHELL_BY_ID;
  const { [threadId]: _removedSession, ...threadSessionById } =
    state.threadSessionById ?? EMPTY_THREAD_SESSION_BY_ID;
  const { [threadId]: _removedTurnState, ...threadTurnStateById } =
    state.threadTurnStateById ?? EMPTY_THREAD_TURN_STATE_BY_ID;
  const { [threadId]: _removedMessageIds, ...messageIdsByThreadId } =
    state.messageIdsByThreadId ?? EMPTY_MESSAGE_IDS_BY_THREAD;
  const { [threadId]: _removedMessages, ...messageByThreadId } =
    state.messageByThreadId ?? EMPTY_MESSAGE_BY_THREAD;
  const { [threadId]: _removedActivityIds, ...activityIdsByThreadId } =
    state.activityIdsByThreadId ?? EMPTY_ACTIVITY_IDS_BY_THREAD;
  const { [threadId]: _removedActivities, ...activityByThreadId } =
    state.activityByThreadId ?? EMPTY_ACTIVITY_BY_THREAD;
  const { [threadId]: _removedPlanIds, ...proposedPlanIdsByThreadId } =
    state.proposedPlanIdsByThreadId ?? EMPTY_PROPOSED_PLAN_IDS_BY_THREAD;
  const { [threadId]: _removedPlans, ...proposedPlanByThreadId } =
    state.proposedPlanByThreadId ?? EMPTY_PROPOSED_PLAN_BY_THREAD;
  const { [threadId]: _removedDiffIds, ...turnDiffIdsByThreadId } =
    state.turnDiffIdsByThreadId ?? EMPTY_TURN_DIFF_IDS_BY_THREAD;
  const { [threadId]: _removedDiffs, ...turnDiffSummaryByThreadId } =
    state.turnDiffSummaryByThreadId ?? EMPTY_TURN_DIFF_BY_THREAD;
  const { [threadId]: _removedSummary, ...sidebarThreadSummaryById } =
    state.sidebarThreadSummaryById;
  const { [threadId]: _removedDetailHydrated, ...threadDetailHydratedById } =
    state.threadDetailHydratedById ?? EMPTY_THREAD_DETAIL_HYDRATED_BY_ID;
  const { [threadId]: _removedHasMoreHistory, ...hasMoreHistoryByThreadId } =
    state.hasMoreHistoryByThreadId ?? EMPTY_HAS_MORE_HISTORY_BY_THREAD;
  const { [threadId]: _removedOldestCursor, ...oldestHistoryCursorByThreadId } =
    state.oldestHistoryCursorByThreadId ?? EMPTY_OLDEST_HISTORY_CURSOR_BY_THREAD;
  const nextThreadIds = (state.threadIds ?? EMPTY_THREAD_IDS).filter((id) => id !== threadId);
  const nextThreads = state.threads.filter((thread) => thread.id !== threadId);

  if (
    nextThreadIds === state.threadIds &&
    nextThreads === state.threads &&
    sidebarThreadSummaryById === state.sidebarThreadSummaryById
  ) {
    return state;
  }

  return {
    ...state,
    threadIds: nextThreadIds,
    threadShellById,
    threadSessionById,
    threadTurnStateById,
    messageIdsByThreadId,
    messageByThreadId,
    activityIdsByThreadId,
    activityByThreadId,
    proposedPlanIdsByThreadId,
    proposedPlanByThreadId,
    turnDiffIdsByThreadId,
    turnDiffSummaryByThreadId,
    sidebarThreadSummaryById,
    threadDetailHydratedById,
    hasMoreHistoryByThreadId,
    oldestHistoryCursorByThreadId,
    threads: nextThreads,
  };
}

// Drop a project and any thread-scoped state that still points at it.
function removeProjectState(state: AppState, projectId: Project["id"]): AppState {
  const threadIds = new Set<ThreadId>();
  for (const thread of state.threads) {
    if (thread.projectId === projectId) {
      threadIds.add(thread.id);
    }
  }
  for (const shell of Object.values(state.threadShellById ?? EMPTY_THREAD_SHELL_BY_ID)) {
    if (shell.projectId === projectId) {
      threadIds.add(shell.id);
    }
  }

  const nextProjects = state.projects.filter((project) => project.id !== projectId);
  const nextState = [...threadIds].reduce((currentState, threadId) => {
    return removeThreadState(currentState, threadId);
  }, state);

  if (nextProjects === state.projects && nextState === state) {
    return state;
  }

  return nextProjects === nextState.projects
    ? nextState
    : {
        ...nextState,
        projects: nextProjects,
      };
}

function commitThreadProjection(
  state: AppState,
  threadId: ThreadId,
  options?: {
    updateThreadArray?: boolean;
    updateSidebarSummary?: boolean;
  },
): AppState {
  const nextThread = getThreadFromState(state, threadId);
  const previousThread = state.threads.find((thread) => thread.id === threadId);
  if (!nextThread) {
    return state;
  }

  // Let hot-path detail syncs skip array churn without suppressing sidebar freshness.
  const shouldUpdateThreadArray = options?.updateThreadArray ?? true;
  // Sidebar summaries still need the latest title/archive signals for navigation.
  const shouldUpdateSidebarSummary = options?.updateSidebarSummary ?? true;
  const threadExists = previousThread !== undefined;
  const threads = shouldUpdateThreadArray
    ? threadExists
      ? updateThread(state.threads, threadId, (thread) =>
          nextThread === thread ? thread : nextThread,
        )
      : [...state.threads, nextThread]
    : state.threads;

  const previousSummary = state.sidebarThreadSummaryById[threadId];
  const nextSummary = shouldUpdateSidebarSummary
    ? buildSidebarThreadSummary(nextThread, previousSummary)
    : previousSummary;

  if (threads === state.threads && nextSummary === previousSummary) {
    return state;
  }

  return {
    ...state,
    threads,
    sidebarThreadSummaryById:
      nextSummary === previousSummary || nextSummary === undefined
        ? state.sidebarThreadSummaryById
        : {
            ...state.sidebarThreadSummaryById,
            [threadId]: nextSummary,
          },
  };
}

function normalizeSingleTurnDiffSummary(
  incoming: Thread["turnDiffSummaries"][number],
  previous: Thread["turnDiffSummaries"][number] | undefined,
): Thread["turnDiffSummaries"][number] {
  const files = normalizeTurnDiffFiles(incoming.files, previous?.files);
  if (
    previous &&
    previous.turnId === incoming.turnId &&
    previous.completedAt === incoming.completedAt &&
    previous.status === incoming.status &&
    previous.assistantMessageId === incoming.assistantMessageId &&
    previous.checkpointTurnCount === incoming.checkpointTurnCount &&
    previous.checkpointRef === incoming.checkpointRef &&
    previous.files === files
  ) {
    return previous;
  }
  return {
    ...incoming,
    files,
  };
}

function sortTurnDiffSummaries(
  summaries: ReadonlyArray<Thread["turnDiffSummaries"][number]>,
): Thread["turnDiffSummaries"] {
  return [...summaries].toSorted(
    (left, right) =>
      (left.checkpointTurnCount ?? Number.MAX_SAFE_INTEGER) -
        (right.checkpointTurnCount ?? Number.MAX_SAFE_INTEGER) ||
      left.completedAt.localeCompare(right.completedAt) ||
      left.turnId.localeCompare(right.turnId),
  );
}

function checkpointStatusToLatestTurnState(
  status: Thread["turnDiffSummaries"][number]["status"],
): NonNullable<Thread["latestTurn"]>["state"] {
  if (status === "error") {
    return "error";
  }
  if (status === "missing") {
    return "interrupted";
  }
  return "completed";
}

// Preserve proposed-plan linkage across live turn updates until the snapshot catches up.
function buildLatestTurn(params: {
  previous: Thread["latestTurn"];
  turnId: NonNullable<Thread["latestTurn"]>["turnId"];
  state: NonNullable<Thread["latestTurn"]>["state"];
  requestedAt: string;
  startedAt: string | null;
  completedAt: string | null;
  assistantMessageId: NonNullable<Thread["latestTurn"]>["assistantMessageId"];
  sourceProposedPlan?: Thread["pendingSourceProposedPlan"];
}): NonNullable<Thread["latestTurn"]> {
  const sourceProposedPlan =
    params.previous?.turnId === params.turnId
      ? (params.previous.sourceProposedPlan ?? params.sourceProposedPlan)
      : params.sourceProposedPlan;
  return {
    turnId: params.turnId,
    state: params.state,
    requestedAt: params.requestedAt,
    startedAt: params.startedAt,
    completedAt: params.completedAt,
    assistantMessageId: params.assistantMessageId,
    ...(sourceProposedPlan ? { sourceProposedPlan } : {}),
  };
}

function reconcileLatestTurnFromSession(
  thread: Thread,
  session: NonNullable<ReadModelThread["session"]>,
  error: string | null,
): Thread["latestTurn"] {
  if (session.status === "running" && session.activeTurnId !== null) {
    return buildLatestTurn({
      previous: thread.latestTurn,
      turnId: session.activeTurnId,
      state: "running",
      requestedAt:
        thread.latestTurn?.turnId === session.activeTurnId
          ? thread.latestTurn.requestedAt
          : session.updatedAt,
      startedAt:
        thread.latestTurn?.turnId === session.activeTurnId
          ? (thread.latestTurn.startedAt ?? session.updatedAt)
          : session.updatedAt,
      completedAt: null,
      assistantMessageId:
        thread.latestTurn?.turnId === session.activeTurnId
          ? thread.latestTurn.assistantMessageId
          : null,
      sourceProposedPlan: thread.pendingSourceProposedPlan,
    });
  }

  void error;
  return closeLatestTurnForSettledSession(thread.latestTurn, session);
}

function rebindTurnDiffSummariesForAssistantMessage(
  turnDiffSummaries: ReadonlyArray<Thread["turnDiffSummaries"][number]>,
  turnId: Thread["turnDiffSummaries"][number]["turnId"],
  assistantMessageId: NonNullable<Thread["latestTurn"]>["assistantMessageId"],
): Thread["turnDiffSummaries"] {
  let changed = false;
  const nextSummaries = turnDiffSummaries.map((summary) => {
    if (summary.turnId !== turnId || summary.assistantMessageId === assistantMessageId) {
      return summary;
    }
    changed = true;
    return {
      ...summary,
      assistantMessageId: assistantMessageId ?? undefined,
    };
  });
  return changed ? nextSummaries : [...turnDiffSummaries];
}

function retainThreadMessagesAfterRevert(
  messages: ReadonlyArray<ChatMessage>,
  retainedTurnIds: ReadonlySet<string>,
  turnCount: number,
): ChatMessage[] {
  const retainedMessageIds = new Set<string>();
  for (const message of messages) {
    if (message.role === "system") {
      retainedMessageIds.add(message.id);
      continue;
    }
    if (
      message.turnId !== undefined &&
      message.turnId !== null &&
      retainedTurnIds.has(message.turnId)
    ) {
      retainedMessageIds.add(message.id);
    }
  }

  const retainedUserCount = messages.filter(
    (message) => message.role === "user" && retainedMessageIds.has(message.id),
  ).length;
  const missingUserCount = Math.max(0, turnCount - retainedUserCount);
  if (missingUserCount > 0) {
    const fallbackUserMessages = messages
      .filter(
        (message) =>
          message.role === "user" &&
          !retainedMessageIds.has(message.id) &&
          (message.turnId === undefined ||
            message.turnId === null ||
            retainedTurnIds.has(message.turnId)),
      )
      .toSorted(
        (left, right) =>
          left.createdAt.localeCompare(right.createdAt) || left.id.localeCompare(right.id),
      )
      .slice(0, missingUserCount);
    for (const message of fallbackUserMessages) {
      retainedMessageIds.add(message.id);
    }
  }

  const retainedAssistantCount = messages.filter(
    (message) => message.role === "assistant" && retainedMessageIds.has(message.id),
  ).length;
  const missingAssistantCount = Math.max(0, turnCount - retainedAssistantCount);
  if (missingAssistantCount > 0) {
    const fallbackAssistantMessages = messages
      .filter(
        (message) =>
          message.role === "assistant" &&
          !retainedMessageIds.has(message.id) &&
          (message.turnId === undefined ||
            message.turnId === null ||
            retainedTurnIds.has(message.turnId)),
      )
      .toSorted(
        (left, right) =>
          left.createdAt.localeCompare(right.createdAt) || left.id.localeCompare(right.id),
      )
      .slice(0, missingAssistantCount);
    for (const message of fallbackAssistantMessages) {
      retainedMessageIds.add(message.id);
    }
  }

  return messages.filter((message) => retainedMessageIds.has(message.id));
}

function retainThreadActivitiesAfterRevert(
  activities: ReadonlyArray<Thread["activities"][number]>,
  retainedTurnIds: ReadonlySet<string>,
): Thread["activities"] {
  return activities.filter(
    (activity) => activity.turnId === null || retainedTurnIds.has(activity.turnId),
  );
}

function retainThreadProposedPlansAfterRevert(
  proposedPlans: ReadonlyArray<Thread["proposedPlans"][number]>,
  retainedTurnIds: ReadonlySet<string>,
): Thread["proposedPlans"] {
  return proposedPlans.filter(
    (proposedPlan) => proposedPlan.turnId === null || retainedTurnIds.has(proposedPlan.turnId),
  );
}

function applyTurnDiffSummaryToThread(
  thread: Thread,
  summary: Thread["turnDiffSummaries"][number],
): Thread {
  const previousSummary = thread.turnDiffSummaries.find(
    (existingSummary) => existingSummary.turnId === summary.turnId,
  );
  const nextSummary = normalizeSingleTurnDiffSummary(summary, previousSummary);
  if (previousSummary && previousSummary.status !== "missing" && nextSummary.status === "missing") {
    return thread;
  }
  const turnDiffSummaries = previousSummary
    ? thread.turnDiffSummaries.map((existingSummary) =>
        existingSummary.turnId === nextSummary.turnId ? nextSummary : existingSummary,
      )
    : sortTurnDiffSummaries([...thread.turnDiffSummaries, nextSummary]);

  const latestTurn =
    thread.latestTurn === null || thread.latestTurn.turnId === nextSummary.turnId
      ? buildLatestTurn({
          previous: thread.latestTurn,
          turnId: nextSummary.turnId,
          state: checkpointStatusToLatestTurnState(nextSummary.status),
          requestedAt: thread.latestTurn?.requestedAt ?? nextSummary.completedAt,
          startedAt: thread.latestTurn?.startedAt ?? nextSummary.completedAt,
          completedAt: nextSummary.completedAt,
          assistantMessageId: nextSummary.assistantMessageId ?? null,
          sourceProposedPlan: thread.pendingSourceProposedPlan,
        })
      : thread.latestTurn;

  if (
    previousSummary === nextSummary &&
    turnDiffSummaries === thread.turnDiffSummaries &&
    latestTurn === thread.latestTurn &&
    (thread.updatedAt ?? thread.createdAt) >= nextSummary.completedAt
  ) {
    return thread;
  }

  return {
    ...thread,
    turnDiffSummaries:
      arraysShallowEqual(thread.turnDiffSummaries, turnDiffSummaries) &&
      thread.turnDiffSummaries.length === turnDiffSummaries.length
        ? thread.turnDiffSummaries
        : turnDiffSummaries,
    latestTurn,
    updatedAt:
      (thread.updatedAt ?? thread.createdAt) > nextSummary.completedAt
        ? thread.updatedAt
        : nextSummary.completedAt,
  };
}

function deriveThreadStateSignals(
  thread: Thread,
): Pick<
  Thread,
  | "latestUserMessageAt"
  | "hasPendingApprovals"
  | "hasPendingUserInput"
  | "hasActionableProposedPlan"
  | "delegateReportReadAt"
  | "delegationWakeSkippedAt"
> {
  const metadata = deriveThreadSummaryMetadata({
    messages: thread.messages,
    activities: thread.activities,
    proposedPlans: thread.proposedPlans,
    latestTurn: thread.latestTurn,
  });
  return {
    latestUserMessageAt: metadata.latestUserMessageAt,
    hasPendingApprovals: metadata.hasPendingApprovals,
    hasPendingUserInput: metadata.hasPendingUserInput,
    hasActionableProposedPlan: metadata.hasActionableProposedPlan,
    // Never downgrade a receipt the server already sent: a thread held in full
    // has its activities to derive from, but one whose activity list was
    // trimmed would otherwise report a read report as unread again.
    delegateReportReadAt: metadata.delegateReportReadAt ?? thread.delegateReportReadAt ?? null,
    // Same reason, for the same kind of fact: the server's decision not to
    // resume must not be forgotten because the activity that recorded it aged
    // out of the list.
    delegationWakeSkippedAt:
      metadata.delegationWakeSkippedAt ?? thread.delegationWakeSkippedAt ?? null,
  };
}

function withDerivedThreadStateSignals(thread: Thread): Thread {
  const nextSignals = deriveThreadStateSignals(thread);
  if (
    thread.latestUserMessageAt === nextSignals.latestUserMessageAt &&
    thread.hasPendingApprovals === nextSignals.hasPendingApprovals &&
    thread.hasPendingUserInput === nextSignals.hasPendingUserInput &&
    thread.hasActionableProposedPlan === nextSignals.hasActionableProposedPlan &&
    (thread.delegateReportReadAt ?? null) === nextSignals.delegateReportReadAt &&
    (thread.delegationWakeSkippedAt ?? null) === nextSignals.delegationWakeSkippedAt
  ) {
    return thread;
  }
  return {
    ...thread,
    ...nextSignals,
  };
}

// Reported once per threadId (not per event) so a burst of events for a
// thread the client hasn't loaded yet — the normal case right after a
// reconnect, before the thread list catches up — doesn't spam the console.
// Kept module-level to match the `reportedUnknownDiagnostics` pattern used
// server-side for unknown Claude SDK subtypes.
const reportedMissingThreadUpdateIds = new Set<ThreadId>();

function applyThreadUpdate(
  state: AppState,
  threadId: ThreadId,
  updater: (thread: Thread) => Thread,
  options?: {
    updateThreadArray?: boolean;
  },
): AppState {
  const currentThread =
    getThreadFromState(state, threadId) ?? state.threads.find((thread) => thread.id === threadId);
  if (!currentThread) {
    // Previously a silent drop. If this fires repeatedly for a thread that
    // *should* exist, it means domain events are arriving before the
    // thread's own read-model row does (or it was never loaded) — worth
    // being able to see without reconstructing the timeline from prod DB
    // queries, which is what happened last time this went unnoticed.
    if (!reportedMissingThreadUpdateIds.has(threadId)) {
      reportedMissingThreadUpdateIds.add(threadId);
      console.warn(`Ignored update for unknown thread '${threadId}'.`);
    }
    return state;
  }
  const updatedThread = withDerivedThreadStateSignals(updater(currentThread));
  if (updatedThread === currentThread) {
    return state;
  }
  return commitThreadProjection(writeThreadState(state, updatedThread, currentThread), threadId, {
    updateThreadArray: options?.updateThreadArray ?? true,
  });
}

function mergeStreamingMessage(
  existingMessage: ChatMessage,
  incomingMessage: ChatMessage,
): ChatMessage | null {
  const nextText =
    incomingMessage.streaming || incomingMessage.text.length === 0
      ? `${existingMessage.text}${incomingMessage.text}`
      : incomingMessage.text;
  const nextAttachments = incomingMessage.attachments ?? existingMessage.attachments;
  const nextCompletedAt = incomingMessage.streaming
    ? existingMessage.completedAt
    : (incomingMessage.completedAt ?? existingMessage.completedAt);
  const nextTurnId =
    incomingMessage.turnId !== undefined ? incomingMessage.turnId : existingMessage.turnId;
  const nextSource = incomingMessage.source ?? existingMessage.source;

  if (
    existingMessage.text === nextText &&
    existingMessage.streaming === incomingMessage.streaming &&
    existingMessage.attachments === nextAttachments &&
    existingMessage.completedAt === nextCompletedAt &&
    existingMessage.turnId === nextTurnId &&
    existingMessage.source === nextSource
  ) {
    return null;
  }

  return {
    ...existingMessage,
    text: nextText,
    streaming: incomingMessage.streaming,
    ...(nextAttachments ? { attachments: nextAttachments } : {}),
    ...(nextTurnId !== undefined ? { turnId: nextTurnId } : {}),
    ...(nextSource !== undefined ? { source: nextSource } : {}),
    ...(nextCompletedAt !== undefined ? { completedAt: nextCompletedAt } : {}),
  };
}

function applyThreadMessageSentEvent(thread: Thread, event: ThreadMessageSentEvent): Thread {
  const payload = event.payload;
  const incomingMessage = normalizeChatMessage(
    {
      id: payload.messageId,
      role: payload.role,
      text: payload.text,
      turnId: payload.turnId,
      attachments: payload.attachments ?? [],
      streaming: payload.streaming,
      source: payload.source,
      createdAt: payload.createdAt,
      updatedAt: payload.updatedAt,
      // The payload carries no author, but the envelope does: the dispatcher
      // stamps `metadata.actorId` from the authenticated session, which is the
      // same fact the server projection later resolves a name from. Without
      // this, a message written by someone else in a shared thread stays
      // unattributed until the next reload.
      ...(payload.role === "user" && event.metadata.actorId
        ? { authorUserId: event.metadata.actorId }
        : {}),
    },
    thread.messages.find((message) => message.id === payload.messageId),
  );
  const existingIndex = thread.messages.findIndex((message) => message.id === payload.messageId);
  let messages = thread.messages;

  if (existingIndex >= 0) {
    const existingMessage = thread.messages[existingIndex];
    if (!existingMessage) {
      return thread;
    }
    const mergedMessage = mergeStreamingMessage(existingMessage, incomingMessage);
    if (mergedMessage !== null) {
      messages = thread.messages.map((message, index) =>
        index === existingIndex ? mergedMessage : message,
      );
    }
  } else {
    messages = [...thread.messages, incomingMessage].slice(-MAX_THREAD_MESSAGES);
  }

  const turnDiffSummaries =
    payload.role === "assistant" && payload.turnId !== null
      ? rebindTurnDiffSummariesForAssistantMessage(
          thread.turnDiffSummaries,
          payload.turnId,
          payload.messageId,
        )
      : thread.turnDiffSummaries;

  let latestTurn = thread.latestTurn;
  if (
    payload.role === "assistant" &&
    payload.turnId !== null &&
    (thread.latestTurn === null || thread.latestTurn.turnId === payload.turnId)
  ) {
    const previousTurn = thread.latestTurn;
    latestTurn = buildLatestTurn({
      previous: previousTurn,
      turnId: payload.turnId,
      state: payload.streaming
        ? "running"
        : previousTurn?.state === "interrupted"
          ? "interrupted"
          : previousTurn?.state === "error"
            ? "error"
            : "completed",
      requestedAt: previousTurn?.requestedAt ?? payload.createdAt,
      startedAt: previousTurn?.startedAt ?? payload.createdAt,
      completedAt: payload.streaming ? (previousTurn?.completedAt ?? null) : payload.updatedAt,
      assistantMessageId: payload.messageId,
      sourceProposedPlan: thread.pendingSourceProposedPlan,
    });
  }

  const updatedAt =
    thread.updatedAt && thread.updatedAt > payload.updatedAt ? thread.updatedAt : payload.updatedAt;
  if (
    messages === thread.messages &&
    turnDiffSummaries === thread.turnDiffSummaries &&
    latestTurn === thread.latestTurn &&
    updatedAt === thread.updatedAt
  ) {
    return thread;
  }

  return {
    ...thread,
    messages,
    turnDiffSummaries,
    latestTurn,
    updatedAt,
  };
}

// Reported once per event type — a server newer than this client bundle can
// emit a domain event type this switch doesn't know about yet, and every
// occurrence after the first is the same information.
const reportedUnknownOrchestrationEventTypes = new Set<string>();

function applyOrchestrationEvent(
  state: AppState,
  event: OrchestrationEvent,
  options?: {
    updateThreadArray?: boolean;
  },
): AppState {
  switch (event.type) {
    case "project.created":
      return upsertProjectFromReadModel(state, {
        id: event.payload.projectId,
        title: event.payload.title,
        workspaceRoot: event.payload.workspaceRoot,
        defaultModelSelection: event.payload.defaultModelSelection,
        scripts: event.payload.scripts,
        localActions: event.payload.localActions,
        linkedProjectIds: event.payload.linkedProjectIds,
        agentIds: event.payload.agentIds,
        iconFileName: event.payload.iconFileName,
        color: event.payload.color,
        description: event.payload.description,
        createdAt: event.payload.createdAt,
        updatedAt: event.payload.updatedAt,
        deletedAt: null,
      });

    case "project.meta-updated": {
      const existingProject = state.projects.find(
        (project) => project.id === event.payload.projectId,
      );
      if (!existingProject) {
        return state;
      }
      return upsertProjectFromReadModel(state, {
        id: existingProject.id,
        title: event.payload.title ?? existingProject.remoteName,
        workspaceRoot: event.payload.workspaceRoot ?? existingProject.cwd,
        defaultModelSelection:
          event.payload.defaultModelSelection !== undefined
            ? event.payload.defaultModelSelection
            : existingProject.defaultModelSelection,
        scripts: event.payload.scripts ?? existingProject.scripts,
        localActions: event.payload.localActions ?? existingProject.localActions,
        linkedProjectIds: event.payload.linkedProjectIds ?? existingProject.linkedProjectIds,
        agentIds: event.payload.agentIds ?? existingProject.agentIds,
        iconFileName:
          event.payload.iconFileName !== undefined
            ? event.payload.iconFileName
            : existingProject.iconFileName,
        color: event.payload.color !== undefined ? event.payload.color : existingProject.color,
        description:
          event.payload.description !== undefined
            ? event.payload.description
            : existingProject.description,
        createdAt: existingProject.createdAt ?? event.payload.updatedAt,
        updatedAt: event.payload.updatedAt,
        deletedAt: null,
      });
    }

    case "project.deleted": {
      const existingIndex = state.projects.findIndex(
        (project) => project.id === event.payload.projectId,
      );
      if (existingIndex < 0) {
        return state;
      }
      return {
        ...state,
        projects: state.projects.filter((project) => project.id !== event.payload.projectId),
      };
    }

    case "thread.message-sent":
      return applyThreadUpdate(
        state,
        event.payload.threadId,
        (thread) => applyThreadMessageSentEvent(thread, event),
        options,
      );

    case "thread.session-set":
      return applyThreadUpdate(
        state,
        event.payload.threadId,
        (thread) => {
          const session = normalizeThreadSession(event.payload.session, thread.session);
          const error = normalizeThreadErrorMessage(event.payload.session.lastError);
          const latestTurn = reconcileLatestTurnFromSession(thread, event.payload.session, error);
          if (
            session === thread.session &&
            error === thread.error &&
            latestTurn === thread.latestTurn
          ) {
            return thread;
          }
          return {
            ...thread,
            session,
            error,
            latestTurn,
            updatedAt:
              (thread.updatedAt ?? thread.createdAt) > event.occurredAt
                ? thread.updatedAt
                : event.occurredAt,
          };
        },
        options,
      );

    case "thread.turn-interrupt-requested": {
      if (event.payload.turnId === undefined) {
        return state;
      }
      return applyThreadUpdate(
        state,
        event.payload.threadId,
        (thread) => {
          const latestTurn = thread.latestTurn;
          if (latestTurn === null || latestTurn.turnId !== event.payload.turnId) {
            return thread;
          }
          return {
            ...thread,
            latestTurn: buildLatestTurn({
              previous: latestTurn,
              turnId: latestTurn.turnId,
              state: "interrupted",
              requestedAt: latestTurn.requestedAt,
              startedAt: latestTurn.startedAt ?? event.payload.createdAt,
              completedAt: latestTurn.completedAt ?? event.payload.createdAt,
              assistantMessageId: latestTurn.assistantMessageId,
            }),
            updatedAt:
              (thread.updatedAt ?? thread.createdAt) > event.occurredAt
                ? thread.updatedAt
                : event.occurredAt,
          };
        },
        options,
      );
    }

    case "thread.session-stop-requested":
      return applyThreadUpdate(
        state,
        event.payload.threadId,
        (thread) => {
          if (thread.session === null) {
            return thread;
          }
          const latestTurn =
            thread.latestTurn !== null &&
            thread.latestTurn.state === "running" &&
            thread.latestTurn.completedAt === null
              ? buildLatestTurn({
                  previous: thread.latestTurn,
                  turnId: thread.latestTurn.turnId,
                  state: "interrupted",
                  requestedAt: thread.latestTurn.requestedAt,
                  startedAt: thread.latestTurn.startedAt ?? event.payload.createdAt,
                  completedAt: event.payload.createdAt,
                  assistantMessageId: thread.latestTurn.assistantMessageId,
                })
              : thread.latestTurn;
          return {
            ...thread,
            session: {
              ...thread.session,
              status: "closed",
              orchestrationStatus: "stopped",
              activeTurnId: undefined,
              updatedAt: event.payload.createdAt,
            },
            latestTurn,
            updatedAt:
              (thread.updatedAt ?? thread.createdAt) > event.occurredAt
                ? thread.updatedAt
                : event.occurredAt,
          };
        },
        options,
      );

    case "thread.turn-start-requested":
      return applyThreadUpdate(
        state,
        event.payload.threadId,
        (thread) => {
          const modelSelection =
            event.payload.modelSelection !== undefined
              ? normalizeModelSelection(event.payload.modelSelection, thread.modelSelection)
              : thread.modelSelection;
          if (
            modelSelection === thread.modelSelection &&
            thread.runtimeMode === event.payload.runtimeMode &&
            thread.interactionMode === event.payload.interactionMode &&
            thread.pendingSourceProposedPlan === event.payload.sourceProposedPlan &&
            (thread.updatedAt ?? thread.createdAt) >= event.payload.createdAt
          ) {
            return thread;
          }
          return {
            ...thread,
            modelSelection,
            runtimeMode: event.payload.runtimeMode,
            interactionMode: event.payload.interactionMode,
            pendingSourceProposedPlan: event.payload.sourceProposedPlan,
            updatedAt:
              (thread.updatedAt ?? thread.createdAt) > event.payload.createdAt
                ? thread.updatedAt
                : event.payload.createdAt,
          };
        },
        options,
      );

    case "thread.activity-appended":
      return applyThreadUpdate(
        state,
        event.payload.threadId,
        (thread) => {
          const nextActivities = normalizeActivities(
            upsertThreadActivity(thread.activities, event.payload.activity),
            thread.activities,
          );
          if (nextActivities === thread.activities) {
            return thread;
          }
          return {
            ...thread,
            activities: nextActivities,
            updatedAt:
              (thread.updatedAt ?? thread.createdAt) > event.payload.activity.createdAt
                ? thread.updatedAt
                : event.payload.activity.createdAt,
          };
        },
        options,
      );

    case "thread.context-cleared":
      return applyThreadUpdate(
        state,
        event.payload.threadId,
        (thread) => {
          // The marker arrives with the event and is appended like any other
          // activity; the cut-off is what makes everything older disappear.
          const nextActivities = normalizeActivities(
            upsertThreadActivity(thread.activities, event.payload.activity),
            thread.activities,
          );
          // The server stops the provider session as part of a clear. Settling
          // a still-running turn here is what keeps the composer from showing
          // a working row that nothing will ever complete.
          const latestTurn =
            thread.latestTurn !== null &&
            thread.latestTurn.state === "running" &&
            thread.latestTurn.completedAt === null
              ? buildLatestTurn({
                  previous: thread.latestTurn,
                  turnId: thread.latestTurn.turnId,
                  state: "interrupted",
                  requestedAt: thread.latestTurn.requestedAt,
                  startedAt: thread.latestTurn.startedAt ?? event.payload.clearedAt,
                  completedAt: event.payload.clearedAt,
                  assistantMessageId: thread.latestTurn.assistantMessageId,
                })
              : thread.latestTurn;
          return {
            ...thread,
            contextClearedAt: event.payload.clearedAt,
            activities: nextActivities,
            latestTurn,
            ...(thread.session
              ? {
                  session: {
                    ...thread.session,
                    status: "closed" as const,
                    orchestrationStatus: "stopped" as const,
                    activeTurnId: undefined,
                    updatedAt: event.payload.clearedAt,
                  },
                }
              : {}),
            updatedAt:
              (thread.updatedAt ?? thread.createdAt) > event.occurredAt
                ? thread.updatedAt
                : event.occurredAt,
          };
        },
        options,
      );

    case "thread.proposed-plan-upserted":
      return applyThreadUpdate(
        state,
        event.payload.threadId,
        (thread) => {
          const previousPlanIndex = thread.proposedPlans.findIndex(
            (plan) => plan.id === event.payload.proposedPlan.id,
          );
          const nextPlan = normalizeProposedPlans(
            [event.payload.proposedPlan],
            previousPlanIndex >= 0 ? [thread.proposedPlans[previousPlanIndex]!] : undefined,
          )[0];
          if (!nextPlan) {
            return thread;
          }
          const proposedPlans =
            previousPlanIndex >= 0
              ? thread.proposedPlans.map((plan, index) =>
                  index === previousPlanIndex ? nextPlan : plan,
                )
              : [...thread.proposedPlans, nextPlan];
          if (arraysShallowEqual(thread.proposedPlans, proposedPlans)) {
            return thread;
          }
          return {
            ...thread,
            proposedPlans,
            updatedAt:
              (thread.updatedAt ?? thread.createdAt) > event.payload.proposedPlan.updatedAt
                ? thread.updatedAt
                : event.payload.proposedPlan.updatedAt,
          };
        },
        options,
      );

    case "thread.turn-diff-completed":
      return applyThreadUpdate(
        state,
        event.payload.threadId,
        (thread) =>
          applyTurnDiffSummaryToThread(thread, {
            turnId: event.payload.turnId,
            completedAt: event.payload.completedAt,
            status: event.payload.status,
            files: event.payload.files.map((file) => ({
              path: file.path,
              ...(file.kind !== undefined ? { kind: file.kind } : {}),
              ...(file.additions !== undefined ? { additions: file.additions } : {}),
              ...(file.deletions !== undefined ? { deletions: file.deletions } : {}),
            })),
            checkpointRef: event.payload.checkpointRef,
            assistantMessageId: event.payload.assistantMessageId ?? undefined,
            checkpointTurnCount: event.payload.checkpointTurnCount,
          }),
        options,
      );

    case "thread.reverted":
      return applyThreadUpdate(
        state,
        event.payload.threadId,
        (thread) => {
          const turnDiffSummaries = thread.turnDiffSummaries
            .filter(
              (entry) =>
                entry.checkpointTurnCount !== undefined &&
                entry.checkpointTurnCount <= event.payload.turnCount,
            )
            .toSorted(
              (left, right) =>
                (left.checkpointTurnCount ?? Number.MAX_SAFE_INTEGER) -
                (right.checkpointTurnCount ?? Number.MAX_SAFE_INTEGER),
            );
          const retainedTurnIds = new Set(turnDiffSummaries.map((entry) => entry.turnId));
          const messages = retainThreadMessagesAfterRevert(
            thread.messages,
            retainedTurnIds,
            event.payload.turnCount,
          ).slice(-MAX_THREAD_MESSAGES);
          const proposedPlans = retainThreadProposedPlansAfterRevert(
            thread.proposedPlans,
            retainedTurnIds,
          );
          const activities = retainThreadActivitiesAfterRevert(thread.activities, retainedTurnIds);
          const latestCheckpoint = turnDiffSummaries.at(-1) ?? null;

          return {
            ...thread,
            turnDiffSummaries,
            messages,
            proposedPlans,
            activities,
            pendingSourceProposedPlan: undefined,
            latestTurn:
              latestCheckpoint === null
                ? null
                : {
                    turnId: latestCheckpoint.turnId,
                    state: checkpointStatusToLatestTurnState(latestCheckpoint.status),
                    requestedAt: latestCheckpoint.completedAt,
                    startedAt: latestCheckpoint.completedAt,
                    completedAt: latestCheckpoint.completedAt,
                    assistantMessageId: latestCheckpoint.assistantMessageId ?? null,
                  },
            updatedAt:
              (thread.updatedAt ?? thread.createdAt) > event.occurredAt
                ? thread.updatedAt
                : event.occurredAt,
          };
        },
        options,
      );

    // Both of these only ever reach the connection they belong to — the server
    // will not fan them out to anyone else — so there is nothing to check here
    // about whose they are. And neither touches `updatedAt`: settling a thread
    // is not activity on it, and letting it bump the timestamp would reorder
    // the sidebar every time somebody tidied their own list.
    case "thread.settled":
      return applyThreadUpdate(
        state,
        event.payload.threadId,
        (thread) => ({ ...thread, settledAt: event.payload.settledAt }),
        options,
      );

    case "thread.snoozed":
      return applyThreadUpdate(
        state,
        event.payload.threadId,
        (thread) => ({
          ...thread,
          snoozedUntil: event.payload.snoozedUntil,
          // Only when the event carries one. The sweep stamps it; a snooze
          // cleared by hand does not, and must not wipe an earlier mark.
          ...(event.payload.wokenAt !== undefined ? { wokenAt: event.payload.wokenAt } : {}),
        }),
        options,
      );

    // Unlike the two above, this one is not per-person: it is broadcast to
    // everyone who can see the thread, so it does touch the shared row.
    case "thread.recap-updated":
      return applyThreadUpdate(
        state,
        event.payload.threadId,
        (thread) => ({
          ...thread,
          recap: { text: event.payload.text, generatedAt: event.payload.generatedAt },
        }),
        options,
      );

    case "thread.archived":
      return applyThreadUpdate(
        state,
        event.payload.threadId,
        (thread) => ({
          ...thread,
          archivedAt: event.payload.archivedAt ?? event.occurredAt,
          updatedAt: event.payload.updatedAt ?? event.occurredAt,
        }),
        options,
      );

    case "thread.unarchived":
      return applyThreadUpdate(
        state,
        event.payload.threadId,
        (thread) => ({
          ...thread,
          archivedAt: null,
          updatedAt: event.payload.updatedAt ?? event.occurredAt,
        }),
        options,
      );

    // Membership arrives as its own event so an open sidebar reflects a share
    // immediately. A thread that becomes visible for the first time arrives as
    // a shell `thread-upserted` instead — this case only has to keep an
    // already-known thread's list in sync.
    case "thread.user-assigned":
      return applyThreadUpdate(
        state,
        event.payload.threadId,
        (thread) => {
          const current = thread.assignedUserIds ?? [];
          if (current.includes(event.payload.userId)) {
            return thread;
          }
          return {
            ...thread,
            assignedUserIds: [...current, event.payload.userId],
            updatedAt: event.payload.assignedAt,
          };
        },
        options,
      );

    case "thread.user-unassigned":
      return applyThreadUpdate(
        state,
        event.payload.threadId,
        (thread) => {
          const current = thread.assignedUserIds ?? [];
          if (!current.includes(event.payload.userId)) {
            return thread;
          }
          return {
            ...thread,
            assignedUserIds: current.filter((userId) => userId !== event.payload.userId),
            updatedAt: event.payload.unassignedAt,
          };
        },
        options,
      );

    default: {
      // Previously silent: an event type this reducer doesn't recognize was
      // dropped without a trace, which is exactly how a stuck thread went
      // undiagnosed until someone queried production. Deployments roll out
      // server-first, so a newer event type reaching an older web bundle is
      // an expected transient during a deploy — but if it persists, it
      // means this switch needs a case.
      const eventType = (event as { type: string }).type;
      if (!reportedUnknownOrchestrationEventTypes.has(eventType)) {
        reportedUnknownOrchestrationEventTypes.add(eventType);
        console.warn(`Unhandled orchestration event type '${eventType}'.`, event);
      }
      return state;
    }
  }
}

export function applyOrchestrationEvents(
  state: AppState,
  events: ReadonlyArray<OrchestrationEvent>,
): AppState {
  return applyOrchestrationEventsHotPath(state, events, { updateThreadArray: true });
}

export function applyOrchestrationEventsHotPath(
  state: AppState,
  events: ReadonlyArray<OrchestrationEvent>,
  options?: {
    updateThreadArray?: boolean;
  },
): AppState {
  let nextState = state;
  for (const event of events) {
    nextState = applyOrchestrationEvent(nextState, event, options);
  }
  return nextState;
}

// ── Pure state transition functions ────────────────────────────────────

export function syncServerShellSnapshot(
  state: AppState,
  snapshot: OrchestrationShellSnapshot,
): AppState {
  rememberProjectUiState(state.projects);
  const projects = mapProjectsFromShellSnapshot(snapshot.projects, state.projects);
  const snapshotThreadIds = new Set(snapshot.threads.map((thread) => thread.id));
  // A thread opened from the directory is not in this snapshot and never will
  // be — the shell is membership only. Rebuilding the tables from the snapshot
  // alone would drop the conversation currently on screen, so the foreign ones
  // are carried across untouched: their rows came from their own detail
  // subscription and stay authoritative until it ends.
  // Same claim the shell stream makes, applied in bulk: anything this snapshot
  // carries is theirs, whatever it was marked as a moment ago.
  const foreignThreadIds = new Set(
    [...(state.foreignThreadIds ?? EMPTY_FOREIGN_THREAD_IDS)].filter(
      (threadId) => !snapshotThreadIds.has(threadId),
    ),
  );
  const nextThreadIds =
    foreignThreadIds.size === 0
      ? snapshotThreadIds
      : new Set([...snapshotThreadIds, ...foreignThreadIds]);

  let normalizedState: AppState = {
    ...state,
    threadIds: [...foreignThreadIds],
    threadShellById: retainThreadScopedRecord(state.threadShellById, foreignThreadIds),
    threadSessionById: retainThreadScopedRecord(state.threadSessionById, foreignThreadIds),
    threadTurnStateById: retainThreadScopedRecord(state.threadTurnStateById, foreignThreadIds),
    messageIdsByThreadId: retainThreadScopedRecord(state.messageIdsByThreadId, nextThreadIds),
    messageByThreadId: retainThreadScopedRecord(state.messageByThreadId, nextThreadIds),
    activityIdsByThreadId: retainThreadScopedRecord(state.activityIdsByThreadId, nextThreadIds),
    activityByThreadId: retainThreadScopedRecord(state.activityByThreadId, nextThreadIds),
    proposedPlanIdsByThreadId: retainThreadScopedRecord(
      state.proposedPlanIdsByThreadId,
      nextThreadIds,
    ),
    proposedPlanByThreadId: retainThreadScopedRecord(state.proposedPlanByThreadId, nextThreadIds),
    turnDiffIdsByThreadId: retainThreadScopedRecord(state.turnDiffIdsByThreadId, nextThreadIds),
    turnDiffSummaryByThreadId: retainThreadScopedRecord(
      state.turnDiffSummaryByThreadId,
      nextThreadIds,
    ),
    threadDetailHydratedById: retainThreadScopedRecord(
      state.threadDetailHydratedById,
      nextThreadIds,
    ),
    hasMoreHistoryByThreadId: retainThreadScopedRecord(
      state.hasMoreHistoryByThreadId,
      nextThreadIds,
    ),
    oldestHistoryCursorByThreadId: retainThreadScopedRecord(
      state.oldestHistoryCursorByThreadId,
      nextThreadIds,
    ),
  };

  for (const thread of snapshot.threads) {
    const previousThread = getThreadFromState(state, thread.id);
    normalizedState = writeThreadShellProjection(
      normalizedState,
      normalizeThreadShellSnapshot(thread, previousThread),
    );
  }

  const derivedThreads = getThreadsFromState(normalizedState);
  const threads = arraysShallowEqual(state.threads, derivedThreads)
    ? state.threads
    : derivedThreads;
  const nextSidebarThreadSummaryById = Object.fromEntries(
    threads.map((thread) => [
      thread.id,
      buildSidebarThreadSummary(thread, state.sidebarThreadSummaryById[thread.id]),
    ]),
  ) as Record<string, SidebarThreadSummary>;
  const sidebarThreadSummaryById = recordsShallowEqual(
    state.sidebarThreadSummaryById,
    nextSidebarThreadSummaryById,
  )
    ? state.sidebarThreadSummaryById
    : nextSidebarThreadSummaryById;

  return {
    ...normalizedState,
    projects,
    threads,
    foreignThreadIds,
    sidebarThreadSummaryById,
    threadsHydrated: true,
  };
}

/**
 * Point a thread's history window at what a fresh snapshot carries.
 *
 * A snapshot replaces the thread's collections outright, so any page the user
 * had scrolled back to is gone from the store with it — and the window has to
 * follow, or the client would keep paging from a cursor for rows it no longer
 * holds and leave a hole in the middle of the transcript.
 *
 * Deliberately a reset rather than a merge of the old history with the new
 * snapshot. Merging is tempting (a reconnect would not lose the user's place)
 * but it has to reason about the `/clear` and compaction floor: rows below a
 * cut-off that fired while we were disconnected would be resurrected by exactly
 * the rows we chose to keep. Re-snapshots only happen on a lost push or a
 * reconnect, so the cheap, always-correct answer wins over the clever one.
 */
function writeThreadHistoryWindow(
  state: AppState,
  threadId: ThreadId,
  history: ThreadHistoryWindow | undefined,
): AppState {
  const previousHasMore = (state.hasMoreHistoryByThreadId ?? EMPTY_HAS_MORE_HISTORY_BY_THREAD)[
    threadId
  ];
  const previousCursor = (state.oldestHistoryCursorByThreadId ??
    EMPTY_OLDEST_HISTORY_CURSOR_BY_THREAD)[threadId];
  // No window argument at all means the caller does not deal in history (the
  // non-hot-path sync, used where the transcript is rebuilt wholesale); leave
  // whatever is there alone. A *server* that omits the fields is different: the
  // caller still passes the object, both fields read `undefined`, and the
  // window correctly collapses to "nothing older to fetch".
  if (history === undefined) return state;
  const nextHasMore = history.hasMoreHistory ?? false;
  const nextCursor = history.oldestCursor;
  if (previousHasMore === nextHasMore && previousCursor === nextCursor) {
    return state;
  }
  const { [threadId]: _dropped, ...cursorsWithoutThread } =
    state.oldestHistoryCursorByThreadId ?? EMPTY_OLDEST_HISTORY_CURSOR_BY_THREAD;
  return {
    ...state,
    hasMoreHistoryByThreadId: {
      ...(state.hasMoreHistoryByThreadId ?? EMPTY_HAS_MORE_HISTORY_BY_THREAD),
      [threadId]: nextHasMore,
    },
    oldestHistoryCursorByThreadId:
      nextCursor === undefined
        ? cursorsWithoutThread
        : { ...cursorsWithoutThread, [threadId]: nextCursor },
  };
}

function syncServerThreadDetailWithOptions(
  state: AppState,
  thread: ReadModelThread,
  options?: {
    updateThreadArray?: boolean;
    history?: ThreadHistoryWindow;
  },
): AppState {
  const previousThread =
    getThreadFromState(state, thread.id) ?? state.threads.find((entry) => entry.id === thread.id);
  const nextThreadDetail =
    options?.updateThreadArray === false
      ? mergeReadModelThreadDetailWithLiveHotPath(thread, previousThread)
      : thread;
  return writeThreadHistoryWindow(
    markThreadDetailHydrated(
      commitThreadProjection(
        writeThreadState(
          state,
          normalizeThreadFromReadModel(nextThreadDetail, previousThread),
          previousThread,
        ),
        thread.id,
        {
          updateThreadArray: options?.updateThreadArray ?? true,
        },
      ),
      thread.id,
    ),
    thread.id,
    options?.history,
  );
}

export function syncServerThreadDetail(
  state: AppState,
  thread: ReadModelThread,
  history?: ThreadHistoryWindow,
): AppState {
  return syncServerThreadDetailWithOptions(state, thread, {
    updateThreadArray: true,
    ...(history ? { history } : {}),
  });
}

export function syncServerThreadDetailHotPath(
  state: AppState,
  thread: ReadModelThread,
  history?: ThreadHistoryWindow,
): AppState {
  return syncServerThreadDetailWithOptions(state, thread, {
    updateThreadArray: false,
    ...(history ? { history } : {}),
  });
}

/**
 * Splice an older page of history onto the front of a thread.
 *
 * Prepends rather than merges-and-sorts: the server returns each stream in the
 * same order the snapshot uses, and the page is strictly older than everything
 * held, so concatenation already yields the right order. Activities keep the
 * server's runtime ordering, which puts `sequence`-less legacy rows first
 * within the page — harmless, because `deriveTimelineEntries` sorts the whole
 * transcript by `createdAt` before rendering.
 *
 * Dedupe by id anyway: a page that races a live event for the same row must not
 * double it, and the cost is one Set over rows we are already walking.
 *
 * The `MAX_THREAD_MESSAGES` cap that the live paths apply is deliberately not
 * applied here. That cap trims from the front, which is precisely what this
 * function just added — capping would make "load older" silently do nothing on
 * a long thread. The cap still guards the live path, where growth is unbounded
 * and unattended; this path only grows when the user keeps asking it to.
 */
export function prependThreadHistory(
  state: AppState,
  threadId: ThreadId,
  page: OrchestrationLoadOlderThreadHistoryResult,
): AppState {
  const nextState = applyThreadUpdate(
    state,
    threadId,
    (thread) => {
      const knownMessageIds = new Set(thread.messages.map((message) => message.id));
      const olderMessages = page.messages
        .filter((message) => !knownMessageIds.has(message.id))
        .map((message) => normalizeChatMessage(message, undefined));
      const knownActivityIds = new Set(thread.activities.map((activity) => activity.id));
      const olderActivities = page.activities.filter(
        (activity) => !knownActivityIds.has(activity.id),
      );
      if (olderMessages.length === 0 && olderActivities.length === 0) {
        return thread;
      }
      return {
        ...thread,
        ...(olderMessages.length > 0 ? { messages: [...olderMessages, ...thread.messages] } : {}),
        ...(olderActivities.length > 0
          ? { activities: [...olderActivities, ...thread.activities] }
          : {}),
      };
    },
    { updateThreadArray: false },
  );
  // The window moves even when the page turned out to be all duplicates:
  // the server still told us where the history now ends, and not advancing
  // would ask for the same rows forever.
  return writeThreadHistoryWindow(nextState, threadId, {
    hasMoreHistory: page.hasMoreHistory,
    oldestCursor: page.oldestCursor,
  });
}

export function applyShellEvent(state: AppState, event: OrchestrationShellStreamEvent): AppState {
  switch (event.kind) {
    case "project-upserted":
      return upsertProjectFromShell(state, event.project);
    case "project-removed":
      return removeProjectState(state, event.projectId);
    case "thread-upserted": {
      // The shell only ever carries this person's own threads, so hearing about
      // one here settles the question: whatever the route guessed while it was
      // waiting for the snapshot, this thread is theirs and belongs in their
      // sidebar. Self-correcting on purpose — a thread they just created can be
      // opened before the shell has caught up with it.
      const claimed = unmarkThreadForeign(state, event.thread.id);
      const nextState = writeThreadShellProjection(
        claimed,
        normalizeThreadShellSnapshot(event.thread, getThreadFromState(claimed, event.thread.id)),
      );
      return commitThreadProjection(nextState, event.thread.id);
    }
    case "thread-removed":
      // The shell stream rewrites "you are not a member of this" into a removal,
      // and a thread opened from the directory is precisely that — so the
      // removal has to be ignored for it, or the conversation on screen would
      // be torn out of the store the first time the server mentioned it.
      // Losing *access* to a foreign thread is not expressible here anyway: the
      // reader never had membership to lose.
      return (state.foreignThreadIds ?? EMPTY_FOREIGN_THREAD_IDS).has(event.threadId)
        ? state
        : removeThreadState(state, event.threadId);
  }
}

export function syncServerReadModel(state: AppState, readModel: OrchestrationReadModel): AppState {
  rememberProjectUiState(state.projects);
  const projects = mapProjectsFromReadModel(
    readModel.projects.filter((project) => project.deletedAt === null),
    state.projects,
  );
  const existingThreadById = new Map(state.threads.map((thread) => [thread.id, thread] as const));
  const nextThreads = readModel.threads
    .filter((thread) => thread.deletedAt === null)
    .map((thread) => {
      const existing = existingThreadById.get(thread.id);
      return normalizeThreadFromReadModel(thread, existing);
    });
  const nextThreadIds = new Set(nextThreads.map((thread) => thread.id));
  let normalizedState: AppState = {
    ...state,
    threadIds: [],
    threadShellById: retainThreadScopedRecord(state.threadShellById, nextThreadIds),
    threadSessionById: retainThreadScopedRecord(state.threadSessionById, nextThreadIds),
    threadTurnStateById: retainThreadScopedRecord(state.threadTurnStateById, nextThreadIds),
    messageIdsByThreadId: retainThreadScopedRecord(state.messageIdsByThreadId, nextThreadIds),
    messageByThreadId: retainThreadScopedRecord(state.messageByThreadId, nextThreadIds),
    activityIdsByThreadId: retainThreadScopedRecord(state.activityIdsByThreadId, nextThreadIds),
    activityByThreadId: retainThreadScopedRecord(state.activityByThreadId, nextThreadIds),
    proposedPlanIdsByThreadId: retainThreadScopedRecord(
      state.proposedPlanIdsByThreadId,
      nextThreadIds,
    ),
    proposedPlanByThreadId: retainThreadScopedRecord(state.proposedPlanByThreadId, nextThreadIds),
    turnDiffIdsByThreadId: retainThreadScopedRecord(state.turnDiffIdsByThreadId, nextThreadIds),
    turnDiffSummaryByThreadId: retainThreadScopedRecord(
      state.turnDiffSummaryByThreadId,
      nextThreadIds,
    ),
    threadDetailHydratedById: retainThreadScopedRecord(
      state.threadDetailHydratedById,
      nextThreadIds,
    ),
    hasMoreHistoryByThreadId: retainThreadScopedRecord(
      state.hasMoreHistoryByThreadId,
      nextThreadIds,
    ),
    oldestHistoryCursorByThreadId: retainThreadScopedRecord(
      state.oldestHistoryCursorByThreadId,
      nextThreadIds,
    ),
  };
  for (const thread of nextThreads) {
    normalizedState = markThreadDetailHydrated(
      writeThreadState(normalizedState, thread),
      thread.id,
    );
  }
  const derivedThreads = getThreadsFromState(normalizedState);
  const threads = arraysShallowEqual(state.threads, derivedThreads)
    ? state.threads
    : derivedThreads;
  const nextSidebarThreadSummaryById = Object.fromEntries(
    threads.map((thread) => [
      thread.id,
      buildSidebarThreadSummary(thread, state.sidebarThreadSummaryById[thread.id]),
    ]),
  ) as Record<string, SidebarThreadSummary>;
  const sidebarThreadSummaryById = recordsShallowEqual(
    state.sidebarThreadSummaryById,
    nextSidebarThreadSummaryById,
  )
    ? state.sidebarThreadSummaryById
    : nextSidebarThreadSummaryById;
  if (
    projects === state.projects &&
    threads === state.threads &&
    sidebarThreadSummaryById === state.sidebarThreadSummaryById &&
    normalizedState.threadIds === state.threadIds &&
    normalizedState.threadShellById === state.threadShellById &&
    normalizedState.threadSessionById === state.threadSessionById &&
    normalizedState.threadTurnStateById === state.threadTurnStateById &&
    normalizedState.messageIdsByThreadId === state.messageIdsByThreadId &&
    normalizedState.messageByThreadId === state.messageByThreadId &&
    normalizedState.activityIdsByThreadId === state.activityIdsByThreadId &&
    normalizedState.activityByThreadId === state.activityByThreadId &&
    normalizedState.proposedPlanIdsByThreadId === state.proposedPlanIdsByThreadId &&
    normalizedState.proposedPlanByThreadId === state.proposedPlanByThreadId &&
    normalizedState.turnDiffIdsByThreadId === state.turnDiffIdsByThreadId &&
    normalizedState.turnDiffSummaryByThreadId === state.turnDiffSummaryByThreadId &&
    normalizedState.threadDetailHydratedById === state.threadDetailHydratedById &&
    state.threadsHydrated
  ) {
    return state;
  }
  return {
    ...normalizedState,
    projects,
    threads,
    sidebarThreadSummaryById,
    threadsHydrated: true,
  };
}

/**
 * Fold the caller's own inbox state into threads already in the store.
 *
 * Runs once per connection, after the snapshot: the two arrive on different
 * channels and in no guaranteed order, so a state for a thread that is not
 * here yet is dropped rather than kept aside. It will come back — this is
 * re-fetched on every reconnect, and a thread that is not in the snapshot is
 * one the sidebar is not showing.
 */
export function applyThreadUserStates(
  state: AppState,
  states: ReadonlyArray<{
    readonly threadId: ThreadId;
    readonly settledAt: string | null;
    readonly snoozedUntil: string | null;
    readonly lastVisitedAt: string | null;
    readonly wokenAt: string | null;
  }>,
): AppState {
  let next = state;
  for (const entry of states) {
    next = applyThreadUpdate(next, entry.threadId, (thread) => ({
      ...thread,
      settledAt: entry.settledAt,
      snoozedUntil: entry.snoozedUntil,
      wokenAt: entry.wokenAt,
      // Only when the server actually holds one. A person who has never opened
      // this thread on any device has no row, and overwriting the local value
      // with null would mark everything unread on every reconnect.
      ...(entry.lastVisitedAt !== null ? { lastVisitedAt: entry.lastVisitedAt } : {}),
    }));
  }
  return next;
}

/**
 * Apply a settle or a snooze locally, before the server has confirmed it.
 *
 * The row is the caller's own and the server never rejects one of these on its
 * merits, so the optimistic write is not a guess about the outcome — it is the
 * same value arriving a round trip earlier. If the command does fail, the
 * caller rolls back to what it captured before.
 */
export function applyOptimisticThreadUserState(
  state: AppState,
  threadId: ThreadId,
  patch: { readonly settledAt?: string | null; readonly snoozedUntil?: string | null },
): AppState {
  return applyThreadUpdate(state, threadId, (thread) => ({
    ...thread,
    ...(patch.settledAt !== undefined ? { settledAt: patch.settledAt } : {}),
    ...(patch.snoozedUntil !== undefined ? { snoozedUntil: patch.snoozedUntil } : {}),
  }));
}

export function markThreadVisited(
  state: AppState,
  threadId: ThreadId,
  visitedAt?: string,
): AppState {
  const at = visitedAt ?? new Date().toISOString();
  const visitedAtMs = Date.parse(at);
  return applyThreadUpdate(state, threadId, (thread) => {
    const previousVisitedAtMs = thread.lastVisitedAt ? Date.parse(thread.lastVisitedAt) : NaN;
    if (
      Number.isFinite(previousVisitedAtMs) &&
      Number.isFinite(visitedAtMs) &&
      previousVisitedAtMs >= visitedAtMs
    ) {
      return thread;
    }
    return { ...thread, lastVisitedAt: at };
  });
}

export function markThreadUnread(state: AppState, threadId: ThreadId): AppState {
  return applyThreadUpdate(state, threadId, (thread) => {
    // Shared with whoever persists this to the server: both sides have to reach
    // the same instant, or the next connection reads back a value that quietly
    // undoes the mark.
    const unreadVisitedAt = resolveMarkUnreadVisitedAt(thread);
    if (unreadVisitedAt === null) return thread;
    if (thread.lastVisitedAt === unreadVisitedAt) return thread;
    return { ...thread, lastVisitedAt: unreadVisitedAt };
  });
}

export function toggleProject(state: AppState, projectId: Project["id"]): AppState {
  return {
    ...state,
    projects: state.projects.map((p) => (p.id === projectId ? { ...p, expanded: !p.expanded } : p)),
  };
}

export function setProjectExpanded(
  state: AppState,
  projectId: Project["id"],
  expanded: boolean,
): AppState {
  let changed = false;
  const projects = state.projects.map((p) => {
    if (p.id !== projectId || p.expanded === expanded) return p;
    changed = true;
    return { ...p, expanded };
  });
  return changed ? { ...state, projects } : state;
}

export function setAllProjectsExpanded(state: AppState, expanded: boolean): AppState {
  let changed = false;
  const projects = state.projects.map((project) => {
    if (project.expanded === expanded) return project;
    changed = true;
    return { ...project, expanded };
  });
  return changed ? { ...state, projects } : state;
}

// Keep just one project expanded so bulk collapse preserves the active chat context.
export function collapseProjectsExcept(
  state: AppState,
  activeProjectId: Project["id"] | null,
): AppState {
  let changed = false;
  const projects = state.projects.map((project) => {
    const nextExpanded = activeProjectId !== null && project.id === activeProjectId;
    if (project.expanded === nextExpanded) return project;
    changed = true;
    return { ...project, expanded: nextExpanded };
  });
  return changed ? { ...state, projects } : state;
}

export function reorderProjects(
  state: AppState,
  draggedProjectId: Project["id"],
  targetProjectId: Project["id"],
): AppState {
  if (draggedProjectId === targetProjectId) return state;
  const draggedIndex = state.projects.findIndex((project) => project.id === draggedProjectId);
  const targetIndex = state.projects.findIndex((project) => project.id === targetProjectId);
  if (draggedIndex < 0 || targetIndex < 0) return state;
  const projects = [...state.projects];
  const [draggedProject] = projects.splice(draggedIndex, 1);
  if (!draggedProject) return state;
  projects.splice(targetIndex, 0, draggedProject);
  return { ...state, projects };
}

export function setError(state: AppState, threadId: ThreadId, error: string | null): AppState {
  return applyThreadUpdate(state, threadId, (thread) => {
    if (thread.error === error) return thread;
    return { ...thread, error };
  });
}

export function setThreadWorkspace(
  state: AppState,
  threadId: ThreadId,
  patch: ThreadWorkspacePatch,
): AppState {
  return applyThreadUpdate(state, threadId, (t) => {
    const nextEnvMode = patch.envMode !== undefined ? patch.envMode : t.envMode;
    const nextBranch = resolveThreadBranchRegressionGuard({
      currentBranch: t.branch,
      nextBranch: patch.branch !== undefined ? patch.branch : t.branch,
    });
    const nextWorktreePath = patch.worktreePath !== undefined ? patch.worktreePath : t.worktreePath;
    const nextAssociatedWorktreePath =
      patch.associatedWorktreePath !== undefined
        ? patch.associatedWorktreePath
        : (t.associatedWorktreePath ?? null);
    const nextAssociatedWorktreeBranch =
      patch.associatedWorktreeBranch !== undefined
        ? patch.associatedWorktreeBranch
        : (t.associatedWorktreeBranch ?? null);
    const nextAssociatedWorktreeRef =
      patch.associatedWorktreeRef !== undefined
        ? patch.associatedWorktreeRef
        : (t.associatedWorktreeRef ?? null);
    if (
      t.envMode === nextEnvMode &&
      t.branch === nextBranch &&
      t.worktreePath === nextWorktreePath &&
      (t.associatedWorktreePath ?? null) === nextAssociatedWorktreePath &&
      (t.associatedWorktreeBranch ?? null) === nextAssociatedWorktreeBranch &&
      (t.associatedWorktreeRef ?? null) === nextAssociatedWorktreeRef
    ) {
      return t;
    }
    const cwdChanged = t.worktreePath !== nextWorktreePath;
    return {
      ...t,
      envMode: nextEnvMode,
      branch: nextBranch,
      worktreePath: nextWorktreePath,
      associatedWorktreePath: nextAssociatedWorktreePath,
      associatedWorktreeBranch: nextAssociatedWorktreeBranch,
      associatedWorktreeRef: nextAssociatedWorktreeRef,
      ...(cwdChanged ? { session: null } : {}),
    };
  });
}

/**
 * Record a thread as held-because-opened. See `AppState.foreignThreadIds`.
 *
 * Called from the route that hydrates an unknown thread, and only there: a
 * thread that arrived in the shell snapshot is this person's own, and marking it
 * would hide it from their own sidebar.
 */
export function markThreadForeign(state: AppState, threadId: ThreadId): AppState {
  const current = state.foreignThreadIds ?? EMPTY_FOREIGN_THREAD_IDS;
  if (current.has(threadId)) {
    return state;
  }
  return { ...state, foreignThreadIds: new Set([...current, threadId]) };
}

/**
 * Forget one, and drop everything the store was holding for it.
 *
 * The rows are removed rather than left behind: nothing on screen refers to them
 * once the reader has navigated away, and a session spent walking the directory
 * would otherwise accumulate every transcript it visited.
 */
/**
 * Drop the foreign mark while keeping everything the store holds for the thread.
 *
 * The counterpart of [[forgetForeignThread]], which also discards the rows: this
 * one runs when the shell claims a thread as this person's own, and throwing
 * away the transcript they are reading would be the opposite of what that means.
 */
function unmarkThreadForeign(state: AppState, threadId: ThreadId): AppState {
  const current = state.foreignThreadIds ?? EMPTY_FOREIGN_THREAD_IDS;
  if (!current.has(threadId)) {
    return state;
  }
  const nextForeign = new Set(current);
  nextForeign.delete(threadId);
  return { ...state, foreignThreadIds: nextForeign };
}

export function forgetForeignThread(state: AppState, threadId: ThreadId): AppState {
  const current = state.foreignThreadIds ?? EMPTY_FOREIGN_THREAD_IDS;
  if (!current.has(threadId)) {
    return state;
  }
  const nextForeign = new Set(current);
  nextForeign.delete(threadId);
  return removeThreadState({ ...state, foreignThreadIds: nextForeign }, threadId);
}

// ── Zustand store ────────────────────────────────────────────────────

interface AppStore extends AppState {
  syncServerShellSnapshot: (snapshot: OrchestrationShellSnapshot) => void;
  syncServerThreadDetail: (thread: ReadModelThread, history?: ThreadHistoryWindow) => void;
  syncServerThreadDetailHotPath: (thread: ReadModelThread, history?: ThreadHistoryWindow) => void;
  prependThreadHistory: (
    threadId: ThreadId,
    page: OrchestrationLoadOlderThreadHistoryResult,
  ) => void;
  syncServerReadModel: (readModel: OrchestrationReadModel) => void;
  applyShellEvent: (event: OrchestrationShellStreamEvent) => void;
  applyOrchestrationEvents: (events: ReadonlyArray<OrchestrationEvent>) => void;
  applyOrchestrationEventsHotPath: (events: ReadonlyArray<OrchestrationEvent>) => void;
  applyThreadUserStates: (
    states: ReadonlyArray<{
      readonly threadId: ThreadId;
      readonly settledAt: string | null;
      readonly snoozedUntil: string | null;
      readonly lastVisitedAt: string | null;
      readonly wokenAt: string | null;
    }>,
  ) => void;
  applyOptimisticThreadUserState: (
    threadId: ThreadId,
    patch: { readonly settledAt?: string | null; readonly snoozedUntil?: string | null },
  ) => void;
  markThreadVisited: (threadId: ThreadId, visitedAt?: string) => void;
  markThreadUnread: (threadId: ThreadId) => void;
  /**
   * Remember that this thread is held only because the reader opened it, not
   * because it is theirs. See `AppState.foreignThreadIds`.
   */
  markThreadForeign: (threadId: ThreadId) => void;
  /** Forget one, once the reader has navigated away and the subscription is gone. */
  forgetForeignThread: (threadId: ThreadId) => void;
  toggleProject: (projectId: Project["id"]) => void;
  setProjectExpanded: (projectId: Project["id"], expanded: boolean) => void;
  setAllProjectsExpanded: (expanded: boolean) => void;
  collapseProjectsExcept: (activeProjectId: Project["id"] | null) => void;
  reorderProjects: (draggedProjectId: Project["id"], targetProjectId: Project["id"]) => void;
  setError: (threadId: ThreadId, error: string | null) => void;
  setThreadWorkspace: (threadId: ThreadId, patch: ThreadWorkspacePatch) => void;
}

export const useStore = create<AppStore>((set) => ({
  ...readPersistedState(),
  syncServerShellSnapshot: (snapshot) => set((state) => syncServerShellSnapshot(state, snapshot)),
  syncServerThreadDetail: (thread, history) =>
    set((state) => syncServerThreadDetail(state, thread, history)),
  syncServerThreadDetailHotPath: (thread, history) =>
    set((state) => syncServerThreadDetailHotPath(state, thread, history)),
  prependThreadHistory: (threadId, page) =>
    set((state) => prependThreadHistory(state, threadId, page)),
  syncServerReadModel: (readModel) => set((state) => syncServerReadModel(state, readModel)),
  applyShellEvent: (event) => set((state) => applyShellEvent(state, event)),
  applyOrchestrationEvents: (events) => set((state) => applyOrchestrationEvents(state, events)),
  applyOrchestrationEventsHotPath: (events) =>
    set((state) => applyOrchestrationEventsHotPath(state, events, { updateThreadArray: false })),
  applyThreadUserStates: (states) => set((state) => applyThreadUserStates(state, states)),
  applyOptimisticThreadUserState: (threadId, patch) =>
    set((state) => applyOptimisticThreadUserState(state, threadId, patch)),
  markThreadVisited: (threadId, visitedAt) =>
    set((state) => markThreadVisited(state, threadId, visitedAt)),
  markThreadUnread: (threadId) => set((state) => markThreadUnread(state, threadId)),
  markThreadForeign: (threadId) => set((state) => markThreadForeign(state, threadId)),
  forgetForeignThread: (threadId) => set((state) => forgetForeignThread(state, threadId)),
  toggleProject: (projectId) => set((state) => toggleProject(state, projectId)),
  setProjectExpanded: (projectId, expanded) =>
    set((state) => setProjectExpanded(state, projectId, expanded)),
  setAllProjectsExpanded: (expanded) => set((state) => setAllProjectsExpanded(state, expanded)),
  collapseProjectsExcept: (activeProjectId) =>
    set((state) => collapseProjectsExcept(state, activeProjectId)),
  reorderProjects: (draggedProjectId, targetProjectId) =>
    set((state) => reorderProjects(state, draggedProjectId, targetProjectId)),
  setError: (threadId, error) => set((state) => setError(state, threadId, error)),
  setThreadWorkspace: (threadId, patch) =>
    set((state) => setThreadWorkspace(state, threadId, patch)),
}));

// Persist state changes with debouncing to avoid localStorage thrashing
useStore.subscribe((state) => {
  rememberProjectUiState(state.projects);
  debouncedPersistState.maybeExecute(state);
});

// Flush pending writes synchronously before page unload to prevent data loss.
if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", () => {
    persistAppStateNow();
  });
}

export function StoreProvider({ children }: { children: ReactNode }) {
  useEffect(() => {
    persistAppStateNow();
  }, []);
  return createElement(Fragment, null, children);
}
